<template>

  <DataGrid2
      ref="grid"
      v-model="selectionLocal"
      :headerShowReload="false"
      :filtering-mode="filteringMode"
      @reload="refresh"
      @row-dblclicked="bubble('row-dblclicked', $event)"
      @row-selected="bubble('row-selected', $event)"
      @rows-selected="bubble('row-selected', $event)"
      @row-index-selected="bubble('row-index-selected', $event)"
      @rows-indices-selected="bubble('rows-indices-selected', $event)"
      @selection-clear="bubble('selection-clear', $event)"
      @before-sorting="bubble('before-sorting', $event)"
      @after-sorting="bubble('after-sorting', $event)"
      @markReadyToShow="markReadyToShow"

      @add="bubble('add', $event)"
      @delete="bubble('delete', $event)"
      @details="bubble('details', $event)"
      @import="bubble('import', $event)"
      @search="bubble('search', $event)"
      @apply-filter="handleApplyFilter"
      @set-col-for-search="setColForSearch"
      :listOfDynamicPreparedObjectForColumns="listOfDynamicPreparedObjectForColumns"
      :toolbox="toolbox"
      :form="form"
      :context="context"
      :eligibleFields="eligibleFields"
      :reloadDataForDynamicTab="reloadDataForDynamicTab"
      :detailsMasterId="detailsMasterId"
      :editable="editable"
      :add-enabled="addEnabled"
      :show-columns="showColumns"
      filtersColsClasses="w-100"
      :selectionSize.sync="selectionSize"
      :showMultiSelect.sync="showMultiSelect"
      :tabName.sync="tabName"
      :recordsWithChosenFilters="recordsWithChosenFilters"
      :editMultiSelectFromTab.sync="editMultiSelectFromTab"
      :dataForSummaryRow="dataForSummaryRow"
      :excludedIds="excludedIds"
      :onlyIds="onlyIds"
      v-bind="{
              ...$attrs,
              ...$props,
              'name': gridName,
              'items': lists,
              'columns': fields,
              'dblClickEnabled': true,
              'headerShowReload': true
            }"
  >
    <slot name="above-grid"></slot>
    <b-container fluid v-if="hasFilters || hasCluster">
      <b-row>
        <b-col class="pr-0 pb-0 h6" v-if="hasCluster"><span v-text="clusterFieldName"></span>: <span
            v-text="clusterVal"></span></b-col>
      </b-row>
      <b-row v-if="hasFilters">
        <b-col cols="auto" class="pr-0 pt-3">
          <font-awesome-icon icon="filter" transform="grow-3 top-1"></font-awesome-icon>
        </b-col>
        <b-col class="pl-2">
          <multiselect
              ref="multiselect"
              class="pt-1 pb-1"
              :items="allFilters"
              :form="form"
              :permissions="permissions"
              @setup-filters="setupFilters"
              @change-filter="changeFilter"
              :eligibleFields="eligibleFields"
              :context="context"
              v-model="allSelectedFilters"
              :activeFilters="activeFilters"
              :recordsWithChosenFiltersInStreamingMode="recordsWithChosenFiltersInStreamingMode"
              :readOnly="false"
              :allow-add="false"
              @item-removed="filterRemoved"
              @filters-clear="clearQuery"
          >
          </multiselect>
        </b-col>
      </b-row>
    </b-container>

  </DataGrid2>
</template>

<script>
import PermissionsSet from "@/server/PermissionsSet";
import DataGrid2 from "@/components/datagrid/DataGrid2";
import RtModule from "@/utils/RtModule";
import TemporaryList from "@/components/forms/TemporaryList"
import TextFormFieldConfig from "@/server/com/silkplm/engine/forms/schema/definition/TextFormFieldConfig";
import FormDetailsConfig from "@/server/com/silkplm/engine/forms/schema/definition/FormDetailsConfig";
import _ from "lodash";
import {ToolboxButton} from "@/components/navbar/toolbox/ToolboxButton";
import Renderers from "@/components/forms/renderers/Renderers";
import BaseFormConfig from "@/server/com/silkplm/engine/forms/schema/BaseFormConfig";
import GridCellEditor from "@/components/forms/GridCellEditor";
import FormsActions from "@/components/forms/FormsActions";
import FormsAI from "@/components/forms/FormsAI";
import filterRenderers from "@/components/forms/renderers/FilterRenderers";
import DelayedExecution from "@/utils/DelayedExecution";
import Multiselect from "@/components/inputs/controls/Multiselect";
import filterBuilder from "@/components/forms/renderers/filters/FilterBuilder";
import SessionManager, {SILKPLM_FREE} from "@/utils/SessionManager";
import {SCOPE_DEVICE_SIZE} from "@/utils/ConfigManager";
import DialogManager from "@/utils/DialogManager";
import ActivityManager from "@/activity/ActivityManager";
import FormsLoader from "@/components/forms/FormsLoader";
import Rpc from "@/Rpc";
import Product from "@/server/com/silkplm/model/plm/Product";
import Suppliers from "@/server/com/silkplm/model/plm/Suppliers";
import IdFilterSpec from "@/server/com/silkplm/engine/forms/schema/definition/filters/IdFilterSpec";
import * as EntityEditFormService from "@/components/forms/customizations/plm/EntityEditFormService";
import Popup from "@/components/dashboard/Popup.vue";
import Vue from 'vue';

import {
  checkIfEnabledByFeatureFlags,
  checkIfEnabledByOrganizationFormsSettingsForForm,
  checkIfEnabledByOrganizationFormsSettingsForFormField, exportBrandingFolderForOrderFromList,
  findFieldValueFromCompanyCompanyConfigurationFormField,
  findGridColOrderForFormField, createBillOfMaterialsSummary
} from "@/components/forms/customizations/plm/EntityEditFormService";
import {
  afterChangeSelectionLocalSetDisabledForToolboxButtons,
  assignEan,
  downloadXlsxTemplateForImportEntities,
  exportDynamicAttributesToExcel,
  exportAllDynamicAttributesToExcel,
  prepareToSendMarkedEntity,
  resendAttachments,
  updateProductDynamicAttributes
} from "@/components/forms/customizations/plm/EntitiesListService";
import StreamingQuery from "@/components/forms/StreamingQuery";
import StreamingEngine from "@/components/forms/StreamingEngine";
import FeatureFlag from "@/server/com/silkplm/rpc/FeatureFlag";
import SizeInProductInSeason from "@/server/com/silkplm/model/plm/SizeInProductInSeason";
import ColorVersion from "@/server/com/silkplm/model/plm/ColorVersion";
import {
  LocalNotification,
  localNotification,
  showLocalNotification
} from "@/components/notifications/LocalNotification";
import EventBus from "@/utils/EventBus";
import DimensionsRows from "@/server/com/silkplm/model/plm/DimensionsRows";
import OrderDetailsPackageRow from "@/server/com/silkplm/model/plm/OrderDetailsPackageRow";
import ColorVersionBindingPlanning from "@/server/com/silkplm/model/plm/ColorVersionBindingPlanning";
import {getFilteredRecordsFromDatabaseWithRows} from "@/components/forms/customizations/plm/DynamicColumnsService";
import PrintoutManager from "@/PrintoutManager";
import {getCustomToolboxMixinsForList} from "@/components/forms/ListsMixins";
import PlanningProduct from "@/server/com/silkplm/model/plm/PlanningProduct";
import PPSamplesProduct from "@/server/com/silkplm/model/plm/PPSamplesProduct";
import RoutingKeys from "@/server/RoutingKeys";
import ComponentIndex from "@/server/com/silkplm/model/plm/dictionaries/ComponentIndex";
import OrderDetails from "@/server/com/silkplm/model/plm/OrderDetails";
import Order from "@/server/com/silkplm/model/plm/Order";
import TechnologicalDetails from "@/server/com/silkplm/model/plm/dictionaries/TechnologicalDetails";
import ComponentIndexProductComponent from "@/server/com/silkplm/model/plm/ComponentIndexProductComponent";
import BudgetingPlannerPM from "@/server/com/silkplm/model/plm/BudgetingPlannerPM";
import FormHeader from "@/components/forms/FormHeader.vue";
import InquiryTabCore from "@/server/com/silkplm/model/plm/tabInquiry/InquiryTabCore";
import InquiryTabSuppliersCore from "@/server/com/silkplm/model/plm/tabInquiry/InquiryTabSuppliersCore";

const ImportFormData = () => require("@/components/plm/ImportFormData");
const OPTIONS_FK_PRELOAD = "fk_preload";
const OPTIONS_FK_DYNAMIC = "fk_dynamic"
const LIST_EXTENDED_ENTITIES = "LIST EXTENDED ENTITIES";
const LIST_EXTENDED_ENTITIES_WITH_FILTERS = "LIST EXTENDED ENTITIES WITH FILTERS";
const LIST_EXTENDED_ENTITIES_CLUSTERED = "LIST EXTENDED ENTITIES CLUSTERED";
const LIST_EXTENDED_ENTITIES_WITH_FILTERING = "LIST EXTENDED ENTITIES WITH FILTERING";
const LIST_DYNAMIC_EXTENDED_ENTITIES = "LIST DYNAMIC EXTENDED ENTITIES";
const LIST_WITH_DYNAMIC_COLUMNS = "LIST WITH DYNAMIC COLUMNS";
const LIST_VIEW = "LIST VIEW";
const GET_FOREIGN_OPTIONS_EXTENDED = "GET FOREIGN OPTIONS EXTENDED";

export default {
  name: "EntitiesList",
  components: {FormHeader, Multiselect, DataGrid2, Popup},

  created() {
    /**
     * @type {StreamingEngine}
     */

    if (window['_mem_bombs']) {
      this.bomb = new ArrayBuffer(70 * 1024 * 1024);
      console.log("Bomb!!!!");
    }

    this.pendingQuery = null;

    this.refreshDelayedScheduler = new DelayedExecution(() => {
      this.refresh();
    });
    this.$onEvent(ServerEvents.refreshList, async (params) => {
      if (params.fullClassLong === this.form.formClassLong) {
        await this.refresh();
      }
    });
  },

  async mounted() {
    let userId = SessionManager.applicationUser.id;
    this.$subscribe(RoutingKeys.IMAGES_LIMIT_EXCEEDED + "." + userId, () => {
      this.handleImagesLimitExceededAndShowNotification();
    });
    this.$subscribe(RoutingKeys.INSERT_COMPONENT_INDEX_GRID + "." + userId, async componentIndexId => {
      if (this.form.formClassLong === TechnologicalDetails.getRemoteClass()) {
        let techDetailsEntity = this.selectionLocal[0];
        await Rpc.plm.services.DictionaryService.createComponentIndexProductComponent(componentIndexId, techDetailsEntity.productComponent);
      }
    });

    if (this.form.formClassLong === await InquiryTabCore.getRemoteClass()) {
      this.$subscribe(RoutingKeys.DOCUMENT_BASE_COMPANIES + "." + this.detailsMasterId, async message => {
        await this.refresh();
      });
    } else if (this.form.formClassLong === await InquiryTabSuppliersCore.getRemoteClass()) {
      this.$subscribe(RoutingKeys.DOCUMENT_SUPPLIERS + "." + this.detailsMasterId, async message => {
        await this.refresh();
      });
    }

    if ((this.form.formClassLong === PlanningProduct.getRemoteClass() || this.form.formClassLong === PPSamplesProduct.getRemoteClass() ||
            this.form.formClassLong === "com.silkplm.model.plm.OfferView" || this.form.formClassLong === "com.silkplm.model.plm.SampleView") &&
        SessionManager.hasFlag(FeatureFlag.CONTRACTORS_MARKET)) {
      this.$subscribe(RoutingKeys.REFRESH_LIST + "." + this.detailsMasterId, async message => {
        await this.refresh();
      });
    }

    // if (SessionManager.userSubscriptionType === SILKPLM_FREE) {
    //   const ComponentClass = Vue.extend(this.$options.components.Popup);
    //   if (!ComponentClass) return;
    //   const popup = new ComponentClass({
    //     parent: this,
    //   });
    //   const container = document.createElement('div');
    //   document.body.appendChild(container);
    //   popup.$mount(container);
    //   this.popupContainer = container;
    // }
    if (this.form.isView) {
      if (this.form.editableFields.length) {
        this.form.editableFields.forEach(fieldName => {
          this.editableViewFields.push(this.form.rootClassShortId + "_" + fieldName);
        })
      }
    }
    this.language = localStorage.getItem('language') || null;
    this.shouldRemember = localStorage.getItem('shouldRemember') === 'true';

    /** @type {DataGrid2|Vue} */
    this.grid = this.$refs.grid;
    this.gridId = this.$refs.grid?.getGridId();

    let clusteringCase = this.form.clusterByField && !this.formValidatedFieldWithParent && !this.filterData && !this.detailsConfig;
    if (!clusteringCase) {
      await this.readActiveFilters();
    }

    setTimeout(() => {
      this.activity = ActivityManager.findActivity(this.$el);
      this.activity?.onShowCallback(async () => {
        if (this.editButton) {
          this.editButton.toggled = this.editable;
        }
        if (this.requiredRefresh) {
          this.requiredRefresh = false;
          await this.refresh();
        }
      });
    })
    if (!this.activeFilters) {
      this.activeFilters = [];
      this.activeDynamicFilters = [];
    }

    for (const ev of ["selectedItem", "selectedItems", "selection", "selectedIndex", "selectedIndices", "filtersColsClasses"]) {
      this.addProxy("update:" + ev);
    }

    if (!this.form) {
      return;
    }
    if (clusteringCase) {
      /**
       * @type {FormConfig}
       */
      let clusterType = this.form.clusterType;

      let context = {
        allowNullSelection: false,
      }
      let selectActivity = await FormsActions.showSelectList(clusterType, {context, showAdd: false, showDelete: false});
      if (!selectActivity) {
        return;
      }
      let res = await selectActivity.getResult();
      this.readActiveFilters();
      if (res) {
        this.filterData = {
          fieldName: this.form.clusterByField,
          clusterId: res.id
        };

        this.clusterFieldName = this.form.clusterByFieldLabel;
        this.clusterVal = FormsAI.getEntityLabel(FormsLoader.detectFormConfig(this.form.clusterType), res);
        this.hasCluster = true;

      } else {
        this.destroyed = true;
        await ActivityManager.closeTopActivity();
        return;
      }
    }

    this.setForm(this.form);

    let clazz;
    if (this.form.isView) {
      this.setViewWatches();
    } else {
      this.setupStandardWatches();
    }

    this.createToolboxButtons();
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this.keyDown);
    this.permissions.detach();

    if (this.popupContainer && document.body.contains(this.popupContainer)) {
      document.body.removeChild(this.popupContainer);
      this.popupContainer = null;
    }
  },

  methods: {
    clearQuery() {
      this.$refs.grid?.clearQuery();
    },
    observeKeyboard() {
      const activity = ActivityManager.findActivity(this.$el);
      this.keyDown = (event) => {
        if (activity === ActivityManager.getTopActivity()) {
          this.$refs.grid?.onKeyDown(event);
        }
      }
      document.addEventListener("keydown", this.keyDown);
    },
    selectOtherProductsToOrderFilter() {
      EventBus.$emit("filter-products-other-users");
    },

    createOrderForMaterialsButton: async function () {
      await EntityEditFormService.handleCreateOrderForMaterialsButton(
          this.selectionLocal, this.context, this.form,
          this.streamingEngine.query.toFormStreamingQuery(),
          this.streamingEngine.getStreamSelection()
      );
    },

    createBillOfMaterials: async function () {
      await EntityEditFormService.createBillOfMaterialsSummary(this.selectionLocal,
          this.streamingEngine.query.toFormStreamingQuery(),
          this.streamingEngine.getStreamSelection()
      );
    },

    setCustomData(lists) {
      lists.forEach(item => {
        if (item.__meta__) {
          item.__meta__.selected = false;
        }
      });
      this.lists = lists;
      this.list = lists;
      // let index = _.findIndex(this.toolbox, ['label', 'Filters']);
      // this.toolbox = this.toolbox.splice(index,   1);
      // this.toolbox = [];
      // this.$refs.grid.$refs.gridHeader.$refs.sideMenuToggle.connectFillerBox(this.toolbox);
      // this.$refs.sideMenuToggle.connectFillerBox(this.filler);
    },


    async createToolboxButtons() {

      if (this.form.formClassLong === Order.getRemoteClass()) {
        let exportBrandingZipButton = new ToolboxButton().withDisabled(true).withIcon("folder-arrow-down").withLabel($tr("exportBrandingFolder", "Export Branding Folder"))
            .withAction(() => EntityEditFormService.exportBrandingFolderForOrderFromList(this.toolbox, this.selectionLocal, this.streamingEngine));
        this.toolbox.push(exportBrandingZipButton);
      }

      if (this.form.printouts) {
        for (let p in this.form.printouts) {
          let flags = this.form.enabledPrintoutsByFeatureFlags;
          if (flags?.length) {
            let printoutEnabled = checkIfEnabledByFeatureFlags(flags);
            if (!printoutEnabled) {
              break;
            }
          }
          const clazz = this.form.formClassLong;
          const formattedClazz = clazz.replace(/ /g, '.');
          var label = "";
          // TODO wojtek dorobić dynamiczne Tr'y
          if (this.form.printouts[p].includes("Printout")) {
            label = this.form.printouts[p];
          } else {
            label = this.form.printouts[p] + " Printout";
          }
          let printButton = new ToolboxButton().withIcon("print").withLabel(label).withDisabled(true)
              .withAction(async () => {
                if (!SessionManager.hasFlag(FeatureFlag.ASK_FOR_LANGUAGE_FOR_INQUIRY_DIALOG)) {
                  let entity = this.selectionLocal[0];
                  if (!entity.id) {
                    entity = this.streamingEngine.dataGrid.$data.filteredRows[0];
                  }
                  await PrintoutManager.formPrintout(this.form, p, entity, "en",);
                } else {
                  let horizontalTemplate = await Rpc.plm.services.OrderService.checkIfHorizontalTemplateForPrintout(p);
                  let entity = this.selectionLocal[0];
                  if (!entity.id) {
                    entity = this.streamingEngine.dataGrid.$data.filteredRows[0];
                  }
                  this.result = await DialogManager.printoutLanguageDialog(this.shouldRemember, this.language, this.form, p, entity, false, horizontalTemplate);
                  if (this.result.shouldRemember) {
                    this.language = this.result.language;
                  } else {
                    this.language = null;
                  }
                  this.shouldRemember = this.result.shouldRemember;
                  localStorage.setItem('language', this.language);
                  localStorage.setItem('shouldRemember', this.shouldRemember);
                }
              });
          this.toolbox.push(printButton);
          break;
        }
      }

      let isVisibleEanButtonForProductInSeason = (this.gridId === "act-com.silkplm.model.plm.ProductInSeason" &&
          SessionManager.hasFlag(FeatureFlag.SEPARATE_PRODUCT));
      let isVisibleEanButtonEan = false;
      let formClassLong = this.form.formClassLong;
      let enabledFieldBySettings = false;
      if (formClassLong === ColorVersion.getRemoteClass()) {
        enabledFieldBySettings = EntityEditFormService.checkIfEnabledByOrganizationFormsSettingsForFormField(this.form, "defaultEAN");
      }

      if (formClassLong === SizeInProductInSeason.getRemoteClass() || (formClassLong === ColorVersion.getRemoteClass() && enabledFieldBySettings)) {
        isVisibleEanButtonEan = true;
      }
      let assignEanButton = new ToolboxButton().withIcon("barcode").withLabel($tr("assignEan", "Assign EAN")).withAction((btn) =>
          assignEan(this.selectionLocal, this.form.formClassLong)).withVisible(isVisibleEanButtonForProductInSeason || isVisibleEanButtonEan).withDisabled(true);
      this.toolbox.push(assignEanButton);

      this.toolbox.push(new ToolboxButton().withIcon("sync").withDataTestPath('reload-btn').withLabel($tr("reload", "Reload")).withAction(
          () => {
            this.$refs.grid.resetSelectonAndVScroll();
            this.$nextTick(() => {
              this.refresh(false, true);
            });
          }
      ));

      let editEnabledBySecuredFields = EntityEditFormService.findEditEnabledFormBySecuredFields(this.form, this.permissions);

      if (this.form.editableGrid && (!this.form.restrictedWrite || this.permissions[this.effectivePermissionWrite] || editEnabledBySecuredFields)) {
        this.editButton = new ToolboxButton().withIcon("pen").withDataTestPath('multiedit-btn').withLabel($tr("edit", "Edit"))
            .withGroup("g-edit").setToggleType().withToggle(
                (val) => {
                  this.editable = val;
                  this.refreshColumns();
                }
            );

        this.toolbox.push(this.editButton);
      }

      if (this.form.formClassLong === Product.getRemoteClass()) {
        let updateProductDynamicAttributesButton = new ToolboxButton().withIcon("pen-to-square").withGroup("g-edit")
            .withLabel($tr('updateProductDynamicAttributes', 'Update Product Dynamic Attributes')).withDataTestPath('multiedit-attributes-btn').withAction((btn) =>
                updateProductDynamicAttributes(this.selectionLocal, this.form, this.fields, this.streaming, this.streamingEngine));
        this.toolbox.push(updateProductDynamicAttributesButton);
      }

      this.filtersButton =
          new ToolboxButton()
              .withIcon("filters")
              .withIconTransform("left-2")
              .withDataTestPath('filters-btn')
              .withLabel($tr("filters", "Filters"))
              .withAction(async (btn) => await this.openFilterWindow());
      if (this.eligibleFields?.length) {
        this.toolbox.push(this.filtersButton);
      }

      if (this.permissions.IHUB_USER) {
        let integrationButton =
            new ToolboxButton()
                .withIcon("link")
                .withGroup("g-import")
                .withLabel($tr("integrations", "Integrations"))
                .withDataTestPath('integrations-btn')
                .withAction((btn) => this.silkIdToExternalId())
        !window.isCordova && this.toolbox.push(integrationButton);
      }

      if (this.form.writePermitted) {
        let showImport;
        if (this.context?.showImport === false) {
          showImport = false;
        } else {
          showImport = this.form.showImport;
        }

        let importButton = new ToolboxButton().withIcon("file-arrow-up").withGroup("g-import").withLabel($tr("import", "Import"))
            .withAction(async (btn) => await this.openImportFile(this.disableImport)).withVisible(showImport);
        !window.isCordova && this.toolbox.push(importButton);
      }

      let showExport;
      if (this.context?.showExport === false) {
        showExport = false;
      } else {
        showExport = this.form.showExport;
      }

      let exportButton = new ToolboxButton().withIcon("file-export").withGroup("g-import").withLabel($tr("export", "Export")).withDisabled(true)
          .withAction((btn) => this.generateExcel()).withVisible(showExport);
      !window.isCordova && this.toolbox.push(exportButton);

      this.exportButton = exportButton;

      if (this.form.formClassLong === Product.getRemoteClass() || this.form.formClassLong === Suppliers.getRemoteClass() || this.form.formClassLong === ComponentIndex.getRemoteClass()) {
        let buttonLabel = "";
        if (Product.getRemoteClass() === this.form.formClassLong) {
          buttonLabel = $tr('entitiesList.download.xlsx', 'Download a template xlsx file for import NEW Products');
        } else if (ComponentIndex.getRemoteClass() === this.form.formClassLong) {
          buttonLabel = $tr('entitiesList.download.xlsx.component.index', 'Download a template xlsx file for import NEW Component Index');
        } else {
          buttonLabel = $tr('entitiesList.download.xlsx.supplires', 'Download a template xlsx file for import NEW Suppliers');
        }
        let importNewProductXlsxTemplateButton =
            new ToolboxButton()
                .withIcon("file")
                .withGroup("g-export")
                .withLabel(buttonLabel)
                .withAction((btn) => downloadXlsxTemplateForImportEntities(this.form.formClassLong));
        !window.isCordova && this.toolbox.push(importNewProductXlsxTemplateButton);

        if (Product.getRemoteClass() === this.form.formClassLong) {
          let exportDynamicAttributesToExcelButton = new ToolboxButton().withIcon("file-arrow-down")
              .withGroup("g-export").withLabel($tr('exportDynamicAttributesToExcel',
                  'Export Dynamic Attributes to Excel')).withAction((btn) => exportDynamicAttributesToExcel(this.selectionLocal, this.form, this.fields,
                  this.streaming, this.streamingEngine))
          !window.isCordova && this.toolbox.push(exportDynamicAttributesToExcelButton);

          if (SessionManager.hasFlag(FeatureFlag.SHOW_ALL_EXPORT_DYNAMIC_ATTRIBUTES_BUTTON)) {
            let exportAllDynamicAttributesToExcelButton = new ToolboxButton().withIcon("cloud-arrow-down")
                .withGroup("g-export").withLabel($tr('exportAllDynamicAttributesToExcel',
                    'Export All Dynamic Attributes to Excel')).withAction((btn) =>
                    exportAllDynamicAttributesToExcel(this.streamingEngine.query.toFormStreamingQuery(), this.streamingEngine.getStreamSelection()));
            !window.isCordova && this.toolbox.push(exportAllDynamicAttributesToExcelButton);
          }
        }
      }

      if (this.form.viewName === "SimpleBudgetingPlannerPM" || this.form.formClassLong === ColorVersionBindingPlanning.getRemoteClass()) {
        let testButton =
            new ToolboxButton()
                .withIcon("table-list")
                .withGroup("other-group")
                .withDataTestPath('product-of-other-users-btn')
                .withLabel($tr('productsOfOtherUsers', 'Products of Other Users'))
                .withAction((btn) => this.selectOtherProductsToOrderFilter())
                .setToggleType();
        this.toolbox.push(testButton);
      }

      if (this.form.formClass === "TechnologicalDetailsView") {

        let addOrderForMaterialsButton2 =
            new ToolboxButton()
                .withDisabled(true)
                .withIcon("fa-solid fa-list-check")
                .withGroup("other-group")
                .withDataTestPath('add-order-for--raw-materials-btn')
                .withLabel($tr("add.order.for.raw.materials.summary", "Bill of materials (BOM) - Summary"))
                .withAction(async () => {
                  await this.createBillOfMaterials();
                })
                .setToggleType();
        // this.addOrderForMaterialsButton.disabled = true;
        this.toolbox.push(addOrderForMaterialsButton2);

        this.addOrderForMaterialsButton =
            new ToolboxButton()
                .withIcon("table-list")
                .withCustomClass("ml-1")
                .withGroup("other-group2")
                .withDataTestPath('add-order-for--raw-materials-btn')
                .withLabel($tr('add.order.for.raw.materials.button.', 'Add Order for Raw Materials'))
                .withAction(async () => await this.createOrderForMaterialsButton())
                .setToggleType();
        this.addOrderForMaterialsButton.disabled = true;
        this.toolbox.push(this.addOrderForMaterialsButton);
      }

      if (this.form.hasIntegrationRequiredFieldsButton) {
        let message = ""
        let requiredFieldsInIntegrationMap = await Rpc.plm.services.CompanyConfigurationService.findRequiredFieldsForIntegration();
        for (const [formClass, listOfRequiredFields] of Object.entries(requiredFieldsInIntegrationMap)) {
          message += "<b>" + formClass + "</b></br>"
          for (let el of listOfRequiredFields) {
            message += "<li>" + el.__computed__.fieldLabel + "</li>";
          }
        }
        let integrationRequiredFieldsButton = new ToolboxButton().withIcon("eye").withLabel($tr("showIntegration", "Show Integration Required Fields")).withAction(() => DialogManager.info("Integration required fields", "Integration required fields: <br>" + message));
        this.toolbox.push(integrationRequiredFieldsButton);
      }
      let formClass = this.form.formClassLong;
      if (formClass === PlanningProduct.getRemoteClass() || formClass === PPSamplesProduct.getRemoteClass() ||
          formClass === "com.silkplm.model.plm.OfferView" || formClass === "com.silkplm.model.plm.SampleView") {
        if (SessionManager.hasFlag(FeatureFlag.CONTRACTORS_MARKET)) {
          this.sendOfferButton = new ToolboxButton().withIcon("fa-paper-plane").withGroup("g-send")
              .withLabel(formClass === PlanningProduct.getRemoteClass() ? $tr('sendInquiry',
                      'Send Offer Inquiry and attachment with Project Specification') :
                  $tr('orderSample', 'Order Sample'))
              .withDataTestPath('multiedit-attributes-btn').withAction(async (btn) => {
                let selectLocal = this.selectionLocal;
                let selectLocalTmpList = [];
                if (selectLocal.materialize) {
                  await selectLocal.materialize();
                  for (let item of selectLocal) {
                    if (!item.supplierIdIsAccountCreated && !item.isSendInquiry && !item.supplierIdRemovedAndBlockedRoleSupplier) {
                      selectLocalTmpList.push(item);
                    }
                  }

                  if (selectLocalTmpList.length > 0) {
                    let connectedElementOfList = "";
                    for (let item of selectLocalTmpList) {
                      connectedElementOfList += " " + item.supplierIdCompanyFullName + "<br>";
                    }
                    await DialogManager.info($tr("entitiesList.info.no.Send",
                            "Can't send offers"),
                        $tr("entitiesList.info.no.send.list",
                            "You are not able to send the offers: ") + "<br>" + connectedElementOfList +
                        $tr("entitiesList.info.no.send.listOne",
                            " because these suppliers don't have active an accounts"));
                    return;
                  }
                }
                if (formClass === PlanningProduct.getRemoteClass()
                    || formClass === "com.silkplm.model.plm.OfferView") {
                  let confirmed = await DialogManager.yesNo($tr('sendOfferFromMotherCompany',
                          'Send Offer Inquiry and attachment with Project Specification'),
                      $tr('entitiesList.sendOfferFromMotherCompany',
                          'Would you like to send the Offer Inquiry and attachment with Project Specification?'), false);
                  if (confirmed) {
                    await prepareToSendMarkedEntity(selectLocal, "planningProduct");
                    this.$nextTick(() => {
                      this.refresh(false, true);
                      this.sendOfferButton.disabled = true;
                      this.sendAttachmentsButton.disabled = false;
                    });
                  }
                } else if (formClass === PPSamplesProduct.getRemoteClass()
                    || formClass === "com.silkplm.model.plm.SampleView") {
                  let supplierCompanyShortName = "";
                  if (selectLocal.length === 1) {
                    let supplier = Suppliers.load(this.selectionLocal[0].supplierId);
                    supplierCompanyShortName = supplier.companyShortName;
                    if (!supplier.companyShortName) {
                      supplierCompanyShortName = "";
                    }
                  }
                  let confirmed = await DialogManager.yesNo(supplierCompanyShortName.length === 0 ?
                          $tr('sendSampleFromMotherCompany', 'Order Sample') :
                          $tr('sendSampleFromMotherCompany.from', 'Order Sample from') + supplierCompanyShortName,
                      $tr('entitiesList.sendSampleFromMotherCompany', 'Would you like to order the Sample?'), false);
                  if (confirmed) {
                    await prepareToSendMarkedEntity(selectLocal, "PPSamplesProduct");
                    this.$nextTick(() => {
                      this.refresh(false, true);
                      this.sendOfferButton.disabled = true;
                      this.sendAttachmentsButton.disabled = false;
                    });
                  }
                }
              });
          this.sendOfferButton.disabled = true;
          this.toolbox.push(this.sendOfferButton);
        }
      }
      if (SessionManager.hasFlag(FeatureFlag.CONTRACTORS_MARKET)) {
        if (formClass === PlanningProduct.getRemoteClass() || formClass === PPSamplesProduct.getRemoteClass() ||
            formClass === "com.silkplm.model.plm.OfferView" || formClass === "com.silkplm.model.plm.SampleView") {
          this.sendAttachmentsButton = new ToolboxButton().withIcon("fa-solid fa-envelopes-bulk").withGroup("g-send")
              .withLabel(formClass === PlanningProduct.getRemoteClass() ? $tr('resendInquiry',
                  'Resend the attachment with Project Specification') : $tr('sendReportSample',
                  'Send an attachment with a report that includes comments after evaluating the Sample'))
              .withDataTestPath('multiedit-attributes-btn').withAction(async (btn) => {
                let selectLocal = this.selectionLocal;
                let titleDialogManager = "";
                let bodyDialogManager = "";

                if (formClass === PlanningProduct.getRemoteClass()) {
                  //Info dla oferty
                  titleDialogManager = $tr('sendAttachment.inquiry',
                      'Resend the attachment with Project Specification');
                  bodyDialogManager = $tr('entitiesList.send.attachment.inquiry',
                      'Would you like to resend the attachment with Project Specification?');
                } else {
                  //Info dla sampla
                  titleDialogManager = $tr('sendAttachment.sample',
                      'Send an attachment with a report that includes comments after evaluating the Sample');
                  bodyDialogManager = $tr('entitiesList.send.attachment.sample',
                      'Would you like to send an attachment with a report that includes comments after evaluating the Sample?');
                }

                let confirmed = await DialogManager.yesNo(titleDialogManager, bodyDialogManager, false);
                if (confirmed) {
                  let isSample = (formClass === PPSamplesProduct.getRemoteClass() || formClass === "com.silkplm.model.plm.SampleView");
                  await resendAttachments(selectLocal, isSample);
                  this.$nextTick(() => {
                    this.refresh(false, true);
                  });
                }
              });

          this.sendAttachmentsButton.disabled = true;
          this.toolbox.push(this.sendAttachmentsButton);
        }
      }
    },

    applyCustomFiltersToActiveFilters() {
      if (this.filter?.customFilter?.length) {
        for (let filter of this.filter.customFilter) {
          let index = this.activeFilters.findIndex((activeFilter) => activeFilter.fieldLabel === filter.fieldLabel)
          if (index !== -1) {
            this.activeFilters.splice(index, 1);
          }
          this.activeFilters.push(filter);
        }
      } else if (this.filter?.customExcludedEligibleFieldNames?.length) {
        for (let excludedFilterName of this.filter.customExcludedEligibleFieldNames) {
          let index = this.activeFilters.findIndex((activeFilter) => activeFilter.fieldName === excludedFilterName)
          if (index !== -1) {
            this.activeFilters.splice(index, 1);
          }
        }
      }
    },

    async readActiveFilters() {
      let activeFilters = SessionManager.configManager?.load("grid.active.filters." + this.gridId);
      let activeDynamicFilters = SessionManager.configManager?.load("grid.active.dynamic.filters." + this.gridId);
      if (activeFilters) {
        for (let activeFilter of activeFilters) {
          for (let field of this.form.fields) {
            if (activeFilter.fieldName === field.fieldName && !this.activeFilters.find(element => element.fieldName === activeFilter.fieldName)) {
              this.activeFilters.push(activeFilter);
              break;
            }
          }
        }
      }
      this.applyCustomFiltersToActiveFilters();
      if (activeDynamicFilters) {
        for (let activeFilter of activeDynamicFilters) {
          if (this.listOfDynamicPreparedObjectForColumns !== null && this.listOfDynamicPreparedObjectForColumns.length > 0) {
            for (let field of this.listOfDynamicPreparedObjectForColumns) {
              if (activeFilter.fieldLabel === field.name) {
                this.activeFilters.push(activeFilter);
                this.activeDynamicFilters.push(activeFilter);
                break;
              }
            }
          }
        }
      }
      this.allFilters = this.activeFilters?.map(filterEntry => {
        return {
          fieldLabel: filterEntry.fieldLabel,
          records: filterEntry.records
        };
      });
      this.allSelectedFilters = this.activeFilters?.map(filterEntry => filterEntry.fieldLabel);
      this.hasFilters = this.allSelectedFilters?.length > 0;
      this.refreshDelayedScheduler.scheduleExecution();

      let activeFiltersCopy = _.cloneDeep(this.activeFilters);
      for (let activeFilter of activeFiltersCopy) {
        if (activeFilter.filterSpec.idValues?.length !== activeFilter.records?.length) {
          for (let field of this.form.fields) {
            if (activeFilter.fieldName === field.fieldName) {
              this.recordsWithChosenFiltersInStreamingMode = await Rpc.Forms.listExtendedEntitiesByListOfIds(field.foreignClass, activeFilter.filterSpec.idValues);
            }
          }
        }
      }
    },

    setupStandardWatches() {
      this.effectivePermissionWrite = this.form.effectivePermissionWrite;
      this.setupWatchers();
      this.$onDelete(this.form.formClassLong, async (changeEvent) => {
        this.eventSequenceId++;
        if (this.refreshInProgress) {
          this.refreshDelayedScheduler.scheduleExecution();
          return;
        }
        if (this.detailsMode === "NEW") {
          return;
        }
        if (this.streaming) {
          return this.streamingEngine.onDelete(changeEvent);
        }
        this.$refs.grid.removeOne((el) => el.id === changeEvent.id);
      });
      this.$onBulkChange(this.form.formClassLong, async (changeEvent) => {
        this.eventSequenceId++;
        if (this.refreshInProgress) {
          this.refreshDelayedScheduler.scheduleExecution();
          return;
        }
        if (this.detailsMode === "NEW") {
          return;
        }
        if (this.streaming) {
          return this.streamingEngine.onBulkChange(changeEvent);
        }
        await this.refresh();
      });

      this.$onInsert(this.form.formClassLong, async (changeEvent) => {
        this.eventSequenceId++;
        if (this.refreshInProgress) {
          this.refreshDelayedScheduler.scheduleExecution();
          return;
        }
        if (this.detailsMode === "NEW") {
          return;
        }
        if (this.detailsConfig)
          if (changeEvent.instance) {
            if (changeEvent.instance[this.detailsConfig.joiningField] !== this.detailsMasterId) {
              return;
            }
          }
        if (!this.form.effectivePermissionRead || this.permissions[this.form.effectivePermissionRead]) {
          let entity = this.form.isDynamic ?
              await Rpc.Forms.getExtendedDynamicEntity(this.form.formClassLong, changeEvent.id).lock() :
              await Rpc.Forms.getExtendedEntity(this.form.formClassLong, changeEvent.id).lock();

          if (this.streaming) {
            return this.streamingEngine.onInsert(changeEvent, entity);
          }

          if (this.formValidatedFieldWithParent) {
            let parentValue = this.formValidatedFieldWithParent.getParentFkKey();
            let field = this.formValidatedFieldWithParent.formFieldConfig.filteringFieldByParent;

            if (entity[field] == parentValue) {
              this.$refs.grid?.pushRow(entity);
            }
            return;
          }
          if (!this.detailsConfig || entity[this.detailsConfig.joiningField] === this.detailsMasterId) {
            this.$refs.grid?.pushRow(entity);
          }
        }
      });

      this.$onUpdate(this.form.formClassLong, async (changeEvent) => {
        this.eventSequenceId++;
        if (this.refreshInProgress) {
          this.refreshDelayedScheduler.scheduleExecution();
          return;
        }
        if (changeEvent.instance?.__archived) {
          if (this.streaming) {
            return this.streamingEngine.onDelete(changeEvent);
          }
          this.$refs.grid.removeOne((el) => el.id === changeEvent.id);
        }
        if (this.detailsMode === "NEW") {
          return;
        }

        if (this.detailsConfig &&
            changeEvent.instance &&
            changeEvent.instance[this.detailsConfig.joiningField] &&
            changeEvent.instance[this.detailsConfig.joiningField] !== this.detailsMasterId) {
          return;
        }
        if (this.streaming) {
          return this.streamingEngine.onUpdate(changeEvent);
        }
        let index = this.lists.findIndex(item => item.id === changeEvent.id);
        if (index != null && index >= 0) {
          this.lists[index] = changeEvent.instance;
          this.$refs.grid.refreshIndex(index);
        }
      });
    },


    /**
     * @param vueComponent {Vue}
     */
    setupWatchers() {
      if (this.form?.watchedClasses) {
        for (let watchedType of this.form.watchedClasses) {

          this.$onChange(watchedType.className, async (changeEvent) => {
            if (changeEvent.operation === 'd' && !watchedType.delete) {
              return;
            }
            if (changeEvent.operation === 'u' && !watchedType.update) {
              return;
            }
            if (changeEvent.operation === 'i' && !watchedType.insert) {
              return;
            }

            this.eventSequenceId++;
            if (this.refreshInProgress) {
              this.refreshDelayedScheduler.scheduleExecution(); // zbiera refreshe i wykonuje go tylko raz
              return;
            }
            if (this.activity?.isOnTop()) {
              this.refreshDelayedScheduler.scheduleExecution();
              return;
            }
            this.requiredRefresh = true;
          })
        }
      }
    },

    setViewWatches() {
      this.setupWatchers();
      /** @type {FormViewConfig} */
      let formViewConfig = this.form;
      let classes = formViewConfig.watchedClasses;
      let masterClass = formViewConfig.masterClass;
      let masterFormConfig = FormsLoader.detectFormConfig(masterClass);
      this.effectivePermissionWrite = masterFormConfig.effectivePermissionWrite;
      this.$onDelete(masterClass, async (changeEvent) => {
        this.eventSequenceId++;
        if (this.refreshInProgress) {
          this.refreshDelayedScheduler.scheduleExecution();
          return;
        }
        if (this.detailsMode === "NEW") {
          return;
        }
        this.$refs.grid.removeOne((el) => el.id === changeEvent.id);
      });
      this.$onInsert(masterClass, async (changeEvent) => {
        this.eventSequenceId++;
        if (this.refreshInProgress) {
          this.refreshDelayedScheduler.scheduleExecution();
          return;
        }
        if (this.detailsMode === "NEW") {
          return;
        }
        if (this.detailsConfig) {
          if (changeEvent.instance) {
            if (changeEvent.instance[this.detailsConfig.joiningField] !== this.detailsMasterId) {
              return;
            }
          }
        }
        let entities = await Rpc.Forms.listView(formViewConfig.formClassLong,
            null,
            filterBuilder().withValue(formViewConfig.rootClassShortId, changeEvent.id).build());

        for (let entity of entities) {
          if (this.formValidatedFieldWithParent) {
            let parentValue = this.formValidatedFieldWithParent.getParentFkKey();
            let field = this.formValidatedFieldWithParent.formFieldConfig.filteringFieldByParent;

            if (entity[field] === parentValue) {
              this.$refs.grid?.pushRow(entity);
            }
          }
          if (!this.detailsConfig || entity[this.detailsConfig.joiningField] === this.detailsMasterId) {
            this.$refs.grid?.pushRow(entity);
          }
        }
      });

      for (let clazz of classes) {
        this.$onBulkChange(clazz.className, async (changeEvent) => {
          this.eventSequenceId++;
          if (this.refreshInProgress) {
            this.refreshDelayedScheduler.scheduleExecution();
            return;
          }
          if (this.detailsMode === "NEW") {
            return;
          }
          if (this.detailsConfig &&
              changeEvent.instance &&
              changeEvent.instance[this.detailsConfig.joiningField] !== this.detailsMasterId) {
            return;
          }
          this.scheduleRefresh();
        });
        let that = this;
        this.$onUpdate(clazz.className, async (changeEvent) => {
          this.eventSequenceId++;
          if (this.refreshInProgress) {
            this.refreshDelayedScheduler.scheduleExecution();
            return;
          }
          if (this.detailsMode === "NEW") {
            return;
          }

          if (this.detailsConfig &&
              changeEvent.instance &&
              changeEvent.instance[this.detailsConfig.joiningField] !== this.detailsMasterId) {
            return;
          }

          let partForm = FormsLoader.detectFormConfig(clazz, true);
          let key;
          if (partForm) {
            key = partForm.formId + "#" + changeEvent.id;
          } else {
            key = clazz + "#" + changeEvent.id;
          }

          let affectedRow = null;
          let affectedIndex = -1;

          for (let i in this.lists) {
            let item = this.lists[i];
            if (!item.__watched_ids__) {
              Log.warn("no __watched_ids__");
            }
            if (item.__watched_ids__.includes(key)) {
              if (affectedRow) {
                that.scheduleRefresh();
                return;
              }

              affectedRow = item;
              affectedIndex = i;
            }
          }

          if (affectedIndex != null && affectedIndex >= 0) {
            let filter = new filterBuilder();
            for (let i in formViewConfig.members) {
              let part = formViewConfig.members[i];
              let field = part.shortId;
              let partId = affectedRow[field];
              if (partId) {
                filter.withId(formViewConfig.rootClassShortId, partId);
              }
            }

            let entities = await Rpc.Forms.listView(formViewConfig.formClassLong,
                null,
                filter.build());
            if (entities.length !== 1) {
              this.scheduleRefresh();
              return;
            }
            this.lists[affectedIndex] = entities[0];
            this.$refs.grid.refreshIndex(affectedIndex);
          }
        });
      }
    },

    getClusterData() {
      if (this.filterData?.clusterId) {
        return this.filterData;
      }
      return null;
    },
    removeTemporaryInstance(instance) {
      let index = this.lists.findIndex(item => item.__id__ === instance.__id__);
      this.detailsTemporaryList.splice(index, 1);
    },
    updateTemporaryInstance(instance) {
      let index = this.lists.findIndex(item => item.__id__ === instance.__id__);
      this.lists[index] = instance;
      this.$refs.grid.refreshIndex(index);
    },
    addProxy(ev) {
      this.grid?.$on(ev, (data) => {
        this.$emit(ev, data);
      });
    },
    bubble(ev, data) {
      this.$emit(ev, data);
    },

    /**
     * @param {FormConfig} val
     */
    setForm(val) {
      if (!this.grid) {
        return;
      }
      if (this.form?.formClassLong === val.formClassLong && this.formInitialized) {
        return;
      }

      if (!this.formInitialized) {
        this.selectionLocal = this.selection;
      }

      this.setupFilters();

      this.formInitialized = true;
      this.refreshDelayedScheduler.scheduleExecution();
    },

    setupFilters() {
      if (!this.form.enabledFilters) {
        return;
      }
      this.eligibleFields = [];
      for (let fld of this.form.fields) {
        if (fld.securedFieldConfig?.restrictedRead &&
            !this.permissions[fld.securedFieldConfig.effectivePermissionRead]) {
          continue;
        }
        if (fld.filterable && !fld.filterDisabled && !fld.gridColDisabled && this.form.clusterByField !== fld.fieldName) {
          let enabledFieldBySettings = EntityEditFormService.checkIfEnabledByOrganizationFormsSettingsForFormField(this.form, fld);
          let enabledFieldByFeatureFlag = EntityEditFormService.checkIfEnabledByFeatureFlags(fld.enabledByFeatureFlags);
          if (enabledFieldBySettings && enabledFieldByFeatureFlag) {
            this.eligibleFields.push(fld);
          }
        }
      }
      for (let dynamic of this.listOfDynamicPreparedObjectForColumns) {
        let dynamicFieldConfigCopy = _.cloneDeep(dynamic.fieldConfig);
        dynamicFieldConfigCopy.fieldLabel = dynamic.name;
        dynamicFieldConfigCopy.fieldName = dynamic.id;
        this.eligibleFields.push(dynamicFieldConfigCopy);
      }

      this.eligibleFields.sort((fA, fB) => {
        return fA.fieldLabel.localeCompare(fB.fieldLabel);
      });

      if (!this.eligibleFields.length) {
        if (this.filtersButton) {
          this.filtersButton.disabled = true;
        }
      }
    },

    scheduleRefresh() {
      this.refreshDelayedScheduler.scheduleExecution();
    },

    async checkScenario() {
      if (this.formValidatedFieldWithParent) {
        // karta z lista elementow typu Details
        return GET_FOREIGN_OPTIONS_EXTENDED;
      } else if (this.form.isView) {
        return LIST_VIEW;
        // view
      } else if (this.form.isDynamic) {
        // dynamic entity
        return LIST_DYNAMIC_EXTENDED_ENTITIES;
      } else if (this.filterData?.instance) {
        // z customowym filtrem
        return LIST_EXTENDED_ENTITIES_WITH_FILTERING
      } else if (this.filterData?.clusterId) {
        // z klastrowanie (+filtry na kolumnach)
        return LIST_EXTENDED_ENTITIES_CLUSTERED;
      } else if (this.filterData?.filterSpecs) {
        // bez klastrowania z filtrami na kolumnach
        return LIST_EXTENDED_ENTITIES_WITH_FILTERS;
      } else {
        // bez klastrowania z filtrami na kolumnach + dodatkowymi filtrami przekazanymi jako parametr do activity
        return LIST_EXTENDED_ENTITIES;
      }
    },

    setLoadInProgress(val) {
      // console.log(`Setting load progress to ${val}`);

      this.loadInProgress = val;
      if (this.exportButton) {
        this.exportButton.disabled = val;
      }
    },

    async runRefreshIfNeeded(funNameOrStreamingQuery, params, fun, dynamicFun = null) {
      this.$emit("start-loading-data");
      if (this.lastEventSequenceId !== this.eventSequenceId) {
        this.lastEventSequenceId = this.eventSequenceId;
        await new Promise(resolve => { // czeka 100 ms i idzie dalej
          setTimeout(resolve, 100)
        });
        if (this.lastEventSequenceId !== this.eventSequenceId) {
          return; // jeżeli w ciągu 100 ms przyszło inne wywołanie, to niech ono zrobi refresha, a my już nie robimy tego ponownie
        }
      }

      if (funNameOrStreamingQuery instanceof StreamingQuery) {
        // console.log("Streaming mode started");
        this.setLoadInProgress(true);

        if (!this.streamingEngine) {
          this.streamingEngine = new StreamingEngine(this, this.$refs.grid, this.form);
        }
        let enabledFromConfiguration = checkIfEnabledByOrganizationFormsSettingsForForm(this.form.formId);
        if (enabledFromConfiguration) {
          funNameOrStreamingQuery.textQuery = this.$refs.grid?.gridHeader?.lastQuery ?? "";
          if (funNameOrStreamingQuery.textQuery) {
            funNameOrStreamingQuery.activeColumns = [];
            for (let col of this.$refs.grid.renderedColumns) {
              if (col.visible) {
                funNameOrStreamingQuery.activeColumns.push(col.prop);
              }
            }
            if (this.colForSearch) {
              funNameOrStreamingQuery.activeColumns = funNameOrStreamingQuery.activeColumns.filter(element => element === this.colForSearch.id);
            }
          }
          // domyslne sortowanie
          if (!funNameOrStreamingQuery.order.length) {
            let sort = null;

            for (let f of this.form.fields) {
              if (f.typeOfSortOfColumn) {
                if (!sort || sort.fieldName == 'EID') {
                  sort = {
                    colName: f.fieldName,
                    ascend: f.typeOfSortOfColumn == 'asc' || f.typeOfSortOfColumn == 'ascend'
                  }
                }
              }
            }

            if (sort) {
              funNameOrStreamingQuery.order = [sort];
            }
          }
          await this.streamingEngine.executeQuery(funNameOrStreamingQuery);
          this.$emit("loading-data", this.streamingEngine);
        }
        return;
      }

      // non streaming mode
      let funName = funNameOrStreamingQuery;
      this.streamingEngine?.stopStreaming();


      let signature = JSON.stringify([funName, params, this.excludedIds, this.eventSequenceId]);
      if (this.currentQuerySignature === signature) {
        return;
      }
      let querySequence = ++this.pendingQueryId;
      this.refreshInProgress = true;
      this.currentQuerySignature = querySequence;

      let res;
      let resDynamic;
      try {
        this.setLoadInProgress(true);
        this.scheduledRefresh = false;
        if (dynamicFun !== null) {
          resDynamic = await dynamicFun();
        }

        res = await fun();

        if (res instanceof StreamingQuery) {
          // console.log("Streaming mode started");
          if (!this.streamingEngine) {
            this.streamingEngine = new StreamingEngine(this, this.$refs.grid, this.form);
          }
          let enabledFromConfiguration = checkIfEnabledByOrganizationFormsSettingsForForm(this.form.formId);
          if (enabledFromConfiguration) {
            await this.streamingEngine.executeQuery(res);
          }
          return;
        }

        if (this.form.hasSummaryFunction) {
          let functionResult = res;
          res = functionResult.providedListToBeAdded;
          this.dataForSummaryRow = [];
          if (functionResult.preparedDataForSummaryRow !== null) {
            this.dataForSummaryRow.push(functionResult.preparedDataForSummaryRow);
            for (let row of this.dataForSummaryRow) {
              row.__row_conf = {cssClass: "basicSummary"};
            }
          }
        }

        // console.log("X1");
        this.setLoadInProgress(false);

      } finally {
        if (querySequence === this.pendingQueryId) {
          this.currentQuerySignature = "";
          this.refreshInProgress = false;
        }
      }
      if (querySequence === this.pendingQueryId) {
        this.list = res;
        if (this.excludedIds?.length) {
          const idsMap = new Set();
          this.excludedIds.forEach(el => idsMap.add(el));
          res = res.filter(record => !idsMap.has(record.id));
        } else if (this.onlyIds?.length) {
          const idsMap = new Set();
          this.onlyIds.forEach(el => idsMap.add(el));
          res = res.filter(record => idsMap.has(record.id));
        }
        if (dynamicFun !== null && resDynamic.length > 0) {
          res = res.filter(item =>
              resDynamic.find(dynamicItem =>
                  dynamicItem.productInSeasonId === item.id &&
                  Object.keys(item?.__supplement__?.dynamicColumns).some(tableObj =>
                      tableObj === dynamicItem.productInSeasonColumnsId)
              )
          );
        }
        this.lists = res;
      }
    },

    async refresh(fallbackToNonStreaming = false, forcerViewportReset = false) {
      this.scheduledRefresh = false;

      if (this.destroyed) {
        return;
      }
      if (this.form.formClassLong === await OrderDetailsPackageRow.getRemoteClass()) {
        let dynamicRowsFormConfig;
        [this.dynamicRows, dynamicRowsFormConfig] = await getFilteredRecordsFromDatabaseWithRows(this.form, this.detailsMasterId);
        if (!this.dynamicRows && !dynamicRowsFormConfig) {
          return;
        }
      }
      try {
        let params;
        let query;

        // if(this.context?.list){
        //   this.customDataLoader = () => {
        //     return this.context.list;
        //   }
        //   // this.setCustomData(this.context.list);
        //   // return;
        // }
        if (this.customDataLoader || this.context?.list) {
          let refreshOptions = {
            activeFilters: this.activeFilters,
            hasFilters: this.hasFilters,
            textQuery: this.$refs.grid?.gridHeader?.lastQuery ?? ""
          }
          query = "customDataLoader";
          params = [refreshOptions];
          await this.runRefreshIfNeeded(query, params, async () => {
            this.setLoadInProgress(true);
            // consol                                                                                                                                                     e.log("Custom loat in progress");
            let res = await this.customDataLoader?.(this, ...params) ?? this.context.list;
            if (!(res instanceof StreamingQuery)) {
              // console.log("X2");
              this.setLoadInProgress(false);
            }
            return res;
          });
          return;
        } else if (this.customItems.length) {
          this.lists = this.customItems;
          this.scheduledRefresh = false;
          return;
        }
        if (this.detailsMode === "NEW" && this.detailsConfig) {
          this.scheduledRefresh = false;
          return this.refreshInNewDetailsMode();
        }

        if (this.detailsConfig) {
          let scenario = await this.checkScenario();

          switch (scenario) {
            case LIST_EXTENDED_ENTITIES_WITH_FILTERS:
              let filters = [...this.filterData.filterSpecs];
              if (this.hasFilters) {
                filters = [...filters, ...this.activeFilters];
              }
              query = "listDetailsWithColsFilter";
              params = [this.form.formClassLong, this.detailsMasterId, this.detailsConfig.detailsViewClass ?? this.detailsConfig.detailsClass, this.detailsConfig.joiningField, filters];
              await this.runRefreshIfNeeded(query, params, async () => {
                if (this.form.hasSummaryFunction) {
                  return Rpc.Forms.listDetailsWithColsFilterWithSummary(...params);
                } else {
                  return Rpc.Forms.listDetailsWithColsFilter(...params);
                }
              });
              break;

            case LIST_EXTENDED_ENTITIES:
            case LIST_VIEW:
              if (SessionManager.featureFlagsSet[FeatureFlag.STREAMING_LISTS] && !fallbackToNonStreaming && this.form.streamingMode) {
                let sq = new StreamingQuery();

                sq.case = LIST_VIEW;
                sq.dataClass = this.detailsConfig.detailsViewClass ?? this.detailsConfig.detailsClass;
                sq.form = this.form;

                sq.isDetail = true;
                sq.detailsMasterClassLong = this.detailsMasterFormConfig.formClassLong;
                sq.detailsMasterId = this.detailsMasterId;
                sq.detailsJoiningField = this.detailsConfig.joiningField;
                sq.forceViewReset = forcerViewportReset;

                if (this.hasFilters) {
                  sq.colFilterSpec = this.activeFilters;
                }

                await this.runRefreshIfNeeded(sq);
              } else {
                if (this.hasFilters) {
                  params = [this.detailsMasterFormConfig.formClassLong, this.detailsMasterId,
                    this.detailsConfig.detailsViewClass ?? this.detailsConfig.detailsClass, this.detailsConfig.joiningField, this.activeFilters];
                  query = "refreshInNewDetailsMode";
                  await this.runRefreshIfNeeded(query, params, async () => {
                    if (this.form.hasSummaryFunction) {
                      return Rpc.Forms.listDetailsWithColsFilterWithSummary(...params);
                    } else {
                      return Rpc.Forms.listDetailsWithColsFilter(...params);
                    }
                  });
                } else {
                  params = [this.detailsMasterFormConfig.formClassLong,
                    this.detailsMasterId,
                    this.detailsConfig.detailsViewClass ?? this.detailsConfig.detailsClass,
                    this.detailsConfig.joiningField];
                  query = "refreshInNewDetailsMode";
                  await this.runRefreshIfNeeded(query, params, async () => {
                    if (this.form.hasSummaryFunction) {
                      return Rpc.Forms.listDetailsWithSummary(...params);
                    } else {
                      return Rpc.Forms.listDetails(...params);
                    }
                  });
                }
              }
              break;

            case LIST_VIEW:
              query = "listView";
              params = [this.form.formClassLong, this.filterData?.clusterId, this.activeFilters];
              await this.runRefreshIfNeeded(query, params, async () => {
                if (this.form.hasSummaryFunction) {
                  return Rpc.Forms.listViewWithSummary(...params);
                } else {
                  return Rpc.Forms.listView(...params);
                }
              });
              break;
          }

          return;
        }

        let scenario = await this.checkScenario();
        let res = null;

        switch (scenario) {
          case GET_FOREIGN_OPTIONS_EXTENDED:
            // TODO WOJTEK - TEMAT NA KIEDYŚ - dołożyć streamingFilter do select form field configa
            // if (SessionManager.featureFlagsSet[FeatureFlag.STREAMING_LISTS] && !fallbackToNonStreaming && this.form.streamingMode) {
            //   let sq = new StreamingQuery();
            //   sq.case = LIST_EXTENDED_ENTITIES;
            //   sq.dataClass = this.form.formClassLong;
            //   sq.form = this.form;
            //   sq.onlyIds = this.onlyIds;
            //   sq.excludedIds = this.excludedIds;
            //   try {
            //     await this.runRefreshIfNeeded(sq);
            //   } catch (e) {
            //     localNotification($tr('switchToSlowerEngine', 'Switch to slower engine'),
            //         $tr('entitiesList.filter.notYet.implemented',
            //             'This filter not yet implemented in fast version, switching to the slower data engine'), 3000);
            //     Log.fatal(e);
            //     return this.refresh(true);
            //   }
            // }
            // else {
            query = "getForeignOptionsExtended";
            params = [];
            await this.runRefreshIfNeeded(query, params, async () => {
              return await this.formValidatedFieldWithParent.getForeignOptionsExtended(this.activeFilters);
            });
            break;
            // }
          case LIST_VIEW:
            if (SessionManager.featureFlagsSet[FeatureFlag.STREAMING_LISTS] && !fallbackToNonStreaming && this.form.streamingMode) {
              let sq = new StreamingQuery();

              sq.case = LIST_VIEW;
              sq.dataClass = this.form.formClassLong;
              sq.form = this.form;
              sq.isView = true;
              sq.clusteringId = this.filterData?.clusterId;
              sq.onlyIds = this.onlyIds;
              sq.excludedIds = this.excludedIds;
              sq.forceViewReset = forcerViewportReset;

              if (this.hasFilters) {
                sq.colFilterSpec = this.activeFilters;
              }
              try {
                await this.runRefreshIfNeeded(sq);
              } catch (e) {
                localNotification($tr('switchToSlowerEngine', 'Switch to slower engine'),
                    $tr('entitiesList.filter.notYet.implemented',
                        'This filter not yet implemented in fast version, switching to the slower data engine'), 3000);
                Log.fatal(e);
                return this.refresh(true);
              }
            } else {
              query = "listView";
              params = [this.form.formClassLong, this.filterData?.clusterId, this.activeFilters];
              await this.runRefreshIfNeeded(query, params, async () => {
                if (this.form.hasSummaryFunction) {
                  return Rpc.Forms.listViewWithSummary(...params);
                } else {
                  return Rpc.Forms.listView(...params);
                }
              });
            }
            break;

          case LIST_DYNAMIC_EXTENDED_ENTITIES:
            query = "listDynamicExtendedEntities";
            params = [this.form.formClassLong];
            await this.runRefreshIfNeeded(query, params, async () => {
              return Rpc.Forms.listDynamicExtendedEntities(...params);
            });
            break;

          case LIST_EXTENDED_ENTITIES_WITH_FILTERING:
            let args = [this.form.formClassLong, this.filterData.fieldName,
              FormsLoader.detectFormConfig(this.filterData.instance).formClassLong, this.filterData.instance];
            if (this.hasFilters) {
              query = "listExtendedEntitiesWithFilteringWithColFilters";
              params = [...args, this.activeFilters];
              await this.runRefreshIfNeeded(query, params, async () => {
                if (this.form.hasSummaryFunction) {
                  return Rpc.Forms.listExtendedEntitiesWithFilteringWithColFiltersWithSummary(...params);
                } else {
                  return Rpc.Forms.listExtendedEntitiesWithFilteringWithColFilters(...params);
                }
              });
            } else {
              query = "listExtendedEntitiesWithFiltering";
              params = args;
              await this.runRefreshIfNeeded(query, params, async () => {
                if (this.form.hasSummaryFunction) {
                  return Rpc.Forms.listExtendedEntitiesWithFilteringWithSummary(...params);
                } else {
                  return Rpc.Forms.listExtendedEntitiesWithFiltering(...args);
                }
              });
            }
            break;

          case LIST_EXTENDED_ENTITIES_CLUSTERED:
            if (SessionManager.featureFlagsSet[FeatureFlag.STREAMING_LISTS] && !fallbackToNonStreaming && this.form.streamingMode) {
              let sq = new StreamingQuery();

              sq.case = LIST_EXTENDED_ENTITIES_CLUSTERED;
              sq.dataClass = this.form.formClassLong;
              sq.form = this.form;
              sq.onlyIds = this.onlyIds;
              sq.excludedIds = this.excludedIds;
              sq.clusteringId = this.filterData?.clusterId;
              sq.forceViewReset = forcerViewportReset;

              if (this.hasFilters) {
                sq.colFilterSpec = this.activeFilters;
              }
              try {

                await this.runRefreshIfNeeded(sq);
              } catch (e) {
                localNotification($tr('switchToSlowerEngine', 'Switch to slower engine'),
                    $tr('entitiesList.filter.notYet.implemented',
                        'This filter not yet implemented in fast version, switching to the slower data engine'), 3000);
                Log.fatal(e);
                return this.refresh(true);
              }
            } else {
              if (this.hasFilters) {
                query = "listExtendedEntitiesClusteredWithColFilters";
                params = [this.form.formClassLong, this.filterData?.clusterId, this.activeFilters];
                await this.runRefreshIfNeeded(query, params, async () => {
                  if (this.form.hasSummaryFunction) {
                    return Rpc.Forms.listExtendedEntitiesClusteredWithColFiltersWithSummary(...params);
                  } else {
                    return Rpc.Forms.listExtendedEntitiesClusteredWithColFilters(...params);
                  }
                });
              } else {
                query = "listExtendedEntitiesClustered";
                params = [this.form.formClassLong, this.filterData?.clusterId];
                await this.runRefreshIfNeeded(query, params, async () => {
                  if (this.form.hasSummaryFunction) {
                    return Rpc.Forms.listExtendedEntitiesClusteredWithSummary(...params);
                  } else {
                    return Rpc.Forms.listExtendedEntitiesClustered(...params);
                  }
                });
              }
            }
            break;

          case LIST_EXTENDED_ENTITIES_WITH_FILTERS:
            let filters = [...this.filterData.filterSpecs];

            if (SessionManager.featureFlagsSet[FeatureFlag.STREAMING_LISTS] && !fallbackToNonStreaming && this.form.streamingMode) {
              let sq = new StreamingQuery();

              sq.case = LIST_EXTENDED_ENTITIES;
              sq.dataClass = this.form.formClassLong;
              sq.form = this.form;
              sq.onlyIds = this.onlyIds;
              sq.excludedIds = this.excludedIds;
              sq.forceViewReset = forcerViewportReset;

              if (this.hasFilters) {
                filters = [...filters, ...this.activeFilters];
              }
              sq.colFilterSpec = filters;
              try {
                await this.runRefreshIfNeeded(sq);
              } catch (e) {
                localNotification($tr('switchToSlowerEngine', 'Switch to slower engine'),
                    $tr('entitiesList.filter.notYet.implemented',
                        'This filter not yet implemented in fast version, switching to the slower data engine'), 3000);
                Log.fatal(e);
                return this.refresh(true);
              }
            } else {
              if (this.hasFilters) {
                filters = [...filters, ...this.activeFilters];
              }
              query = "listExtendedEntitiesWithColFilters";
              params = [this.form.formClassLong, filters];
              await this.runRefreshIfNeeded(query, params, async () => {
                if (this.form.hasSummaryFunction) {
                  return Rpc.Forms.listExtendedEntitiesWithColFiltersWithSummary(...params);
                } else {
                  return Rpc.Forms.listExtendedEntitiesWithColFilters(...params);
                }
              });
            }
            break;

          case LIST_EXTENDED_ENTITIES:
            let hasFilters = this.hasFilters;
            let activeFilters = [...this.activeFilters];
            if (this.context?.onlyIds?.length) {
              let filterSpec = new IdFilterSpec();
              let includedFilters = {filterSpec, fieldName: "id"};
              filterSpec.idValues = this.context.onlyIds;
              if (!hasFilters) {
                hasFilters = true;
                activeFilters = [includedFilters]
              } else {
                activeFilters.push(includedFilters)
              }
            }
            if (this?.context?.onlyIds?.length === 0) {
              return null;
            }
            if (hasFilters) {
              query = "listExtendedEntitiesWithColFilters";

              let paramsForDynamic = null;
              let activeFiltersForDynamic = [];
              let dynamicForm = null;
              if (this.form.classIsDynamic && this.listOfDynamicPreparedObjectForColumns.length > 0) {

                this.listOfDynamicPreparedObjectForColumns.forEach(obj1 => {
                  const foundObjects = activeFilters.filter(obj2 => obj2.fieldLabel === obj1.name);
                  if (foundObjects.length > 0) {
                    activeFiltersForDynamic = activeFiltersForDynamic.concat(foundObjects);
                    activeFilters = activeFilters.filter(obj => !foundObjects.includes(obj));
                  }
                });

                dynamicForm = FormsLoader.detectFormConfig(this.form.dynamicCells);
                if (dynamicForm != null) {
                  paramsForDynamic = [dynamicForm.formClassLong, activeFiltersForDynamic];
                }
              }

              params = [this.form.formClassLong, activeFilters];

              if (SessionManager.featureFlagsSet[FeatureFlag.STREAMING_LISTS] && !fallbackToNonStreaming && this.form.streamingMode) {
                let sq = new StreamingQuery();

                sq.case = LIST_EXTENDED_ENTITIES;
                sq.dataClass = this.form.formClassLong;
                sq.form = this.form;
                sq.onlyIds = this.onlyIds;
                sq.excludedIds = this.excludedIds;
                sq.colFilterSpec = activeFilters;
                sq.dynamicFilterSpec = activeFiltersForDynamic;
                sq.forceViewReset = forcerViewportReset;

                if (dynamicForm !== null) {
                  sq.dynamicForm = dynamicForm.formClassLong;
                }

                try {
                  await this.runRefreshIfNeeded(sq);
                } catch (e) {
                  localNotification($tr('switchToSlowerEngine', 'Switch to slower engine'),
                      $tr('entitiesList.filter.notYet.implemented',
                          'This filter not yet implemented in fast version, switching to the slower data engine'), 3000);
                  Log.fatal(e);
                  return this.refresh(true);
                }

              } else {
                if (this.form.classIsDynamic && activeFiltersForDynamic.length > 0) {
                  await this.runRefreshIfNeeded(
                      query,
                      params,
                      async () => {
                        if (this.form.hasSummaryFunction) {
                          return Rpc.Forms.listExtendedEntitiesWithColFiltersWithSummary(...params);
                        } else {
                          return Rpc.Forms.listExtendedEntitiesWithColFilters(...params);
                        }
                      },
                      async () => {
                        return Rpc.Forms.listExtendedEntitiesWithColFilters(...paramsForDynamic);
                      });
                } else {
                  await this.runRefreshIfNeeded(
                      query,
                      params,
                      async () => {
                        if (this.form.hasSummaryFunction) {
                          return Rpc.Forms.listExtendedEntitiesWithColFiltersWithSummary(...params);
                        } else {
                          return Rpc.Forms.listExtendedEntitiesWithColFilters(...params);
                        }
                      });
                }
              }
            } else {
              if (SessionManager.featureFlagsSet[FeatureFlag.STREAMING_LISTS] && !fallbackToNonStreaming && this.form.streamingMode) {
                let sq = new StreamingQuery();
                sq.case = LIST_EXTENDED_ENTITIES;
                sq.dataClass = this.form.formClassLong;
                sq.form = this.form;
                sq.onlyIds = this.onlyIds;
                sq.excludedIds = this.excludedIds;
                sq.filteringMode = this.filteringMode;
                sq.forceViewReset = forcerViewportReset;

                try {
                  await this.runRefreshIfNeeded(sq);
                } catch (e) {
                  localNotification($tr('switchToSlowerEngine', 'Switch to slower engine'),
                      $tr('entitiesList.filter.notYet.implemented',
                          'This filter not yet implemented in fast version, switching to the slower data engine'), 3000);
                  Log.fatal(e);
                  return this.refresh(true);
                }
              } else {
                query = "listExtendedEntities";
                params = [this.form.formClassLong];
                await this.runRefreshIfNeeded(query, params, async () => {
                  if (this.form.hasSummaryFunction) {
                    return Rpc.Forms.listExtendedEntitiesWithColFiltersWithSummary(...params);
                  } else {
                    return Rpc.Forms.listExtendedEntities(...params);
                  }
                });
              }
            }
        }

      } finally {
        if (this.scheduledRefresh) {
          this.refreshDelayedScheduler.scheduleExecution();
        }
      }
    },
    async refreshInNewDetailsMode() {
      this.lists = this.detailsTemporaryList?.items ?? [];
    },

    async generateExcel() {
      if (this.form.isView) {
        let fileInRepositoryResult = null;
        if (this.streaming) {
          // handled by delayed tasks
          await Rpc.ExcelExports.listViewFromStream(this.form.formClassLong,
              this.filterData?.clusterId, this.activeFilters, this.streamingEngine.query.toFormStreamingQuery(), this.streamingEngine.getStreamSelection());
          return;

        } else {
          let selectedIdsList = [];
          if (this.selectionLocal && this.selectionLocal.length) {
            this.selectionLocal.forEach(el => selectedIdsList.push(el.__full_id__));
          }
          await Rpc.ExcelExports.listView(this.form.formClassLong,
              this.filterData?.clusterId, this.activeFilters, selectedIdsList);
        }
        return;
      }
      if (this.form.formClass === "BudgetingPlannerPM") {
        let showMultiSelect = this.showMultiSelect;
        let selectedIdsList = null;
        if (this.streaming) {
          let streamSelection = this.streamingEngine.getStreamSelection();
          if (streamSelection.excludedIds) {
            streamSelection.excludedIds = streamSelection.excludedIds.filter(x => x != null);
          }
          if (streamSelection.includedIds) {
            streamSelection.includedIds = streamSelection.includedIds.filter(x => x != null);
          }
          selectedIdsList = await Rpc.FormsStreams.findSelectedIdsStream(streamSelection);
          // console.log("Export with streaming");
          this.$emit("export", {items: selectedIdsList, showMultiSelect, streamSelection});
        } else {
          // console.log("Export with no streaming");
          this.$emit("export", {items: this.selectionLocal, showMultiSelect, streamSelection: null});
        }
        return;
      }

      if (this.form.formClass === "OrderDetailsPackageRow") {
        let orderDetailsEntityFromDatabase = await Rpc.Forms.getExtendedEntity(this.detailsMasterFormConfig.formClassLong, this.detailsMasterId);
        await Rpc.plm.services.OrderDetailsPackageService.generateReportForOrderDetails(orderDetailsEntityFromDatabase).lock();
        return;
      }

      if (this.form.formClass === "DimensionsRows") {
        let productInSeasonEntityFromDatabase = await Rpc.Forms.getExtendedEntity(this.detailsMasterFormConfig.formClassLong, this.detailsMasterId);
        await Rpc.plm.services.DimensionsService.generateReportForDimensions(productInSeasonEntityFromDatabase).lock();
        return;
      }

      if (this.form.formClass === "ProductGradingDetail") {
        let productGradingDetailFromDatabase = await Rpc.Forms.getExtendedEntity(this.form.formClassLong, this.lists[0].id);
        await Rpc.plm.services.DimensionalService.generateReportForProductGradingDetail(productGradingDetailFromDatabase).lock();
        return;
      }

      let scenario = await this.checkScenario();
      let selectedIdsList = [];
      if (this.streaming) {
        selectedIdsList = await Rpc.FormsStreams.findSelectedIdsStream(this.streamingEngine.getStreamSelection());
      } else {
        if (this.selectionLocal && this.selectionLocal.length) {
          this.selectionLocal.forEach(el => selectedIdsList.push(el.id));
        }
      }

      if (this.$refs.grid.showMultiSelect && this.selectionLocal.length === 0) {
        await DialogManager.info($tr('entitiesList.noRecordsSelected', 'No records selected.'),
            $tr('entitiesList.SelectAtLeastOne', 'Select at least one record to export the data.'))
        return;

      }
      let fileInRepositoryResult = null;
      let masterClazz = this.detailsMasterFormConfig?.formClassLong;
      switch (scenario) {
        case GET_FOREIGN_OPTIONS_EXTENDED:
          // TODO delete?
          await this.formValidatedFieldWithParent.getForeignOptionsExtended();
          break;

        case LIST_DYNAMIC_EXTENDED_ENTITIES:
          let selectedUUids = [];
          if (selectedIdsList) {
            selectedIdsList.forEach(el => selectedUUids.push(el.id))
          }
          let sortedList = this.$refs.grid.$refs.baseGrid.postProcessedSource;
          let finalSelectedList = [];
          sortedList.forEach(el => {
            if (selectedIdsList.includes(el.id)) {
              finalSelectedList.push(el.id);
            }
          });
          if (!finalSelectedList.length) {
            await DialogManager.info($tr('entitiesList.noSelection', 'No selected items.'),
                $tr('entitiesList.noSelection', 'No selected items.'));
            return
          }
          await Rpc.ExcelExports.listDynamicExtendedEntities(this.form.formClassLong, finalSelectedList, this.form.formId);
          break;

        case LIST_EXTENDED_ENTITIES_WITH_FILTERING:
          let args = [masterClazz, this.detailsMasterId, this.form.formId, this.filterData.fieldName,
            FormsLoader.detectFormConfig(this.filterData.instance).formClassLong, this.filterData.instance];
          if (this.hasFilters) {
            if (this.streamingEngine) {
              await Rpc.ExcelExports.listExtendedEntitiesWithFilteringWithColFilters(
                  ...args, this.activeFilters, selectedIdsList, this.streamingEngine.query.toFormStreamingQuery(), this.streamingEngine.getStreamSelection()).lock();
            } else {
              await Rpc.ExcelExports.listExtendedEntitiesWithFilteringWithColFilters(
                  ...args, this.activeFilters, selectedIdsList, null, null).lock();
            }
          } else {
            if (this.streamingEngine) {
              await Rpc.ExcelExports.listExtendedEntitiesWithFiltering(...args, selectedIdsList,
                  this.streamingEngine.query.toFormStreamingQuery(), this.streamingEngine.getStreamSelection()).lock();
            } else {
              await Rpc.ExcelExports.listExtendedEntitiesWithFiltering(...args, selectedIdsList, null, null).lock();
            }
          }
          break;

        case LIST_EXTENDED_ENTITIES_CLUSTERED:
          if (this.hasFilters) {
            if (this.streamingEngine) {
              await Rpc.ExcelExports.listExtendedEntitiesClusteredWithColFilters(
                  masterClazz, this.detailsMasterId, this.form.formId,
                  this.filterData?.clusterId, this.activeFilters, selectedIdsList,
                  this.streamingEngine.query.toFormStreamingQuery(), this.streamingEngine.getStreamSelection()
              ).lock()
            } else {
              await Rpc.ExcelExports.listExtendedEntitiesClusteredWithColFilters(
                  masterClazz, this.detailsMasterId, this.form.formId,
                  this.filterData?.clusterId, this.activeFilters, selectedIdsList, null, null).lock()
            }

          } else {
            if (this.streamingEngine) {
              await Rpc.ExcelExports.listExtendedEntitiesClustered(
                  masterClazz, this.detailsMasterId, this.form.formId, this.filterData?.clusterId, selectedIdsList,
                  this.streamingEngine.query.toFormStreamingQuery(), this.streamingEngine.getStreamSelection()
              ).lock();
            } else {
              await Rpc.ExcelExports.listExtendedEntitiesClustered(
                  masterClazz, this.detailsMasterId, this.form.formId, this.filterData?.clusterId, selectedIdsList, null, null
              ).lock();
            }
          }
          break;

        case LIST_EXTENDED_ENTITIES_WITH_FILTERS:
          let filters = [...this.filterData.filterSpecs];
          if (this.hasFilters) {
            filters = [...filters, ...this.activeFilters];
          }
          if (this.streamingEngine) {
            await Rpc.ExcelExports.listExtendedEntitiesWithColFilters(
                masterClazz, this.detailsMasterId, this.form.formId, filters, selectedIdsList,
                this.streamingEngine.query.toFormStreamingQuery(), this.streamingEngine.getStreamSelection()).lock();
          } else {
            await Rpc.ExcelExports.listExtendedEntitiesWithColFilters(
                masterClazz, this.detailsMasterId, this.form.formId, filters, selectedIdsList, null, null).lock();
          }
          break;

        case LIST_EXTENDED_ENTITIES:
          if (this.hasFilters) {
            if (this.form.isDynamic) {
              this.activeFilters = this.activeFilters.filter((filter) => {
                return !this.activeDynamicFilters.some(
                    (dynamicFilter) => dynamicFilter.fieldName === filter.fieldName
                );
              });

              let dynamicCellFormConfig = FormsLoader.detectFormConfig(this.form.dynamicCells)
              let listOfIds = []
              for (let filter of this.activeDynamicFilters) {
                listOfIds = [...filter.filterSpec.idValues, ...listOfIds]
              }
              let activeDynamicFilters = [{
                "fieldName": dynamicCellFormConfig.cellValueFieldForDynamicColumns,
                "filterSpec": {
                  "idValues": listOfIds
                }
              }];
              if (this.activeDynamicFilters.length <= 0) {
                await Rpc.ExcelExports.listExtendedEntitiesDynamicWithColFilters(
                    masterClazz, this.detailsMasterId, this.form.formId, this.activeFilters, null, selectedIdsList).lock();
              } else {
                await Rpc.ExcelExports.listExtendedEntitiesDynamicWithColFilters(
                    masterClazz, this.detailsMasterId, this.form.formId, this.activeFthis.ilters, activeDynamicFilters, selectedIdsList).lock();
              }
            } else {
              if (this.streamingEngine) {
                await Rpc.ExcelExports.listExtendedEntitiesWithColFilters(
                    masterClazz, this.detailsMasterId, this.form.formId, this.activeFilters, selectedIdsList,
                    this.streamingEngine.query.toFormStreamingQuery(), this.streamingEngine.getStreamSelection()).lock();
              } else {
                await Rpc.ExcelExports.listExtendedEntitiesWithColFilters(
                    masterClazz, this.detailsMasterId, this.form.formId, this.activeFilters, selectedIdsList,
                    null, null).lock();
              }
            }
          } else {
            if (this.streamingEngine) {
              await Rpc.ExcelExports.listExtendedEntities(
                  masterClazz, this.detailsMasterId, this.form.formId, selectedIdsList,
                  this.streamingEngine.query.toFormStreamingQuery(), this.streamingEngine.getStreamSelection()).lock();
            } else {
              await Rpc.ExcelExports.listExtendedEntities(
                  masterClazz, this.detailsMasterId, this.form.formId, selectedIdsList, null, null).lock();
            }

          }
          break;
      }
    },
    DownloadToDevice(fileurl) {
      var blob = null;
      var xhr = new XMLHttpRequest();
      xhr.open("GET", fileurl);
      xhr.responseType = "blob";//force the HTTP response, response-type header to be blob
      xhr.onload = function () {
        blob = xhr.response;//xhr.response is now a blob object
        let storageLocation = "";
        switch (device.platform) {
          case "Android":
            storageLocation = 'file:///storage/emulated/0/';
            break;
          case "iOS":
            storageLocation = cordova.file.documentsDirectory;
            break;
        }
        let folderpath = storageLocation + 'Download';
        const currentDate = new Date();

        const day = String(currentDate.getDate()).padStart(2, '0');
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const year = currentDate.getFullYear();

        const hours = String(currentDate.getHours()).padStart(2, '0');
        const minutes = String(currentDate.getMinutes()).padStart(2, '0');
        const seconds = String(currentDate.getSeconds()).padStart(2, '0');

        const formattedDate = `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;

        let filename = `SilkPLM_${formattedDate}.png`;
        let DataBlob = blob;
        window.resolveLocalFileSystemURL(folderpath, function (dir) {
          dir.getFile(undefined, {create: true}, function (file) {
            file.createWriter(function (fileWriter) {
              fileWriter.write(DataBlob);
              //Download was succesfull
            }, function (err) {
              // failed
              console.log(err);
            });
          });
        });
      }
      xhr.send();

    },
    refreshColumns() {
      if (this.$refs.grid) {
        this.$refs.grid.colsVersion++;
      }
    },

    async openFilterWindow() {
      let result = [];
      let colTable = [];
      let col = null;
      this.addNextFilter = true;
      this.setupFilters();
      while (this.addNextFilter) {
        this.refreshDelayedScheduler.pauseExecution();
        if (!this.eligibleFields) {
          DialogManager.info($tr('filters', 'Filters'),
              $tr('entitiesList.NoColumn.canUser', 'There is no column you can use to setup filters')).then();
          this.refreshDelayedScheduler.resumeExecution();
          return;
        }

        let options = this.eligibleFields.map(value => ({text: value.fieldLabel, value}));

        col = await DialogManager.selectInCombo($tr('selectColumnToFilter', 'Select column to filter'), options);
        if (!col) {
          this.refreshDelayedScheduler.resumeExecution();
          this.addNextFilter = false;
        }
        if (this.addNextFilter) {
          let resultToPush = null;

          if (this.form.classIsDynamic && this.listOfDynamicPreparedObjectForColumns.length > 0) {
            let dynamicForm = FormsLoader.detectFormConfig(this.form.dynamicCells);
            resultToPush = await filterRenderers[col.filterRenderer](col, dynamicForm, this.context, this);
          } else {
            resultToPush = await filterRenderers[col.filterRenderer](col, this.form, this.context, this);
          }

          if (resultToPush && !Array.isArray(resultToPush) || resultToPush && resultToPush.some(item => item !== null)) {
            result.push(resultToPush);
            colTable.push(col);
            if (this.eligibleFields.includes(col)) {
              this.eligibleFields = this.eligibleFields.filter(function (e) {
                return e !== col
              })
            }
          }
          if (this.eligibleFields.length === 0) {
            this.addNextFilter = false;
          } else {
            if (SessionManager.hasFlag(FeatureFlag.STREAMING_LISTS)) {
              this.addNextFilter = false;
            } else {
              this.addNextFilter = await DialogManager.yesNo($tr('addingFilters', 'Adding filters'),
                  $tr('entitiesList.Add.another.filter', 'Would you like to add another filter?'))
            }
          }
        }
      }

      if (!result) {
        this.refreshDelayedScheduler.resumeExecution();
        return;
      }
      if (!this.activeFilters) {
        this.activeFilters = [];
        this.activeDynamicFilters = [];
      }
      this.changeFilter(colTable, result)
      this.setupFilters();
    },

    handleApplyFilter(colTable, result) {
      this.changeFilter(colTable, result);
      this.setupFilters();
    },

    setColForSearch(columnData) {
      this.colForSearch = columnData;
    },

    markReadyToShow() {
      this.$emit("markReadyToShow")
    },

    changeFilter(colTable, result) {
      let filterSpec = null;
      let records = null;

      this.allFilters = this.activeFilters?.map(filterEntry => {
        return {
          fieldLabel: filterEntry.fieldLabel,
          records: filterEntry.records
        };
      });

      result.forEach((value, i) => {
        if (Array.isArray(value) && value.length === 2) {
          [filterSpec, records] = value;
        } else {
          filterSpec = value;
          records = value;
        }
        this.activeFilters = this.activeFilters.filter(item => !(item.fieldName === colTable[i].fieldName));
        this.activeFilters.push({
          records: records,
          fieldName: colTable[i].fieldName,
          fieldLabel: colTable[i].fieldLabel,
          filterSpec
        });
        if (colTable[i].isDynamic) {
          this.activeDynamicFilters = this.activeDynamicFilters.filter(item => !(item.fieldName === colTable[i].fieldName));
          this.activeDynamicFilters.push({
            records: records,
            fieldName: colTable[i].fieldName,
            fieldLabel: colTable[i].fieldLabel,
            filterSpec
          });
        }
        this.allFilters = this.activeFilters.map(filterEntry => {
          return {
            fieldLabel: filterEntry.fieldLabel,
            records: filterEntry.records
          };
        });
        this.allSelectedFilters = this.activeFilters.map(filterEntry => filterEntry.fieldLabel);
      });
      this.hasFilters = this.allSelectedFilters.length > 0;
      this.refreshDelayedScheduler.scheduleExecution();
    },

    filterRemoved(removedVal) {
      this.allSelectedFilters = this.allSelectedFilters.filter(val => val !== removedVal.value);
      this.activeFilters = this.activeFilters.filter(item => item.fieldLabel !== removedVal.value);
      this.activeDynamicFilters = this.activeDynamicFilters.filter(item => item.fieldLabel !== removedVal.value);

      this.hasFilters = this.allSelectedFilters && this.allSelectedFilters.length > 0;
      this.refreshDelayedScheduler.scheduleExecution();
    },

    async silkIdToExternalId() {
      if (!this.selectionLocal?.length) {
        await DialogManager.info($tr('entitiesList.noRecordSelected', 'No record selected.'),
            $tr('entitiesList.select.one', 'Select one record to show information.'));
      }
      if (this.selectionLocal.length > 1) {
        await DialogManager.info($tr('entitiesList.Select.only.one', 'Select only one record.'),
            $tr('entitiesList.select.one.to.show', 'Select one record to show information.'));
      }
      let selectedRecord = this.selectionLocal[0];
      let idMappingList = await Rpc.Integrations.findBindingsWithExternalIds(this.form.formClassLong, selectedRecord.id);
      let message = "";
      for (let idMap of idMappingList) {
        let externalSystem = idMap.externalSystem;
        let externalId = idMap.externalId;
        let silkId = idMap.silkId;
        let encodedSilkId = await Rpc.Integrations.decodedSilkId(silkId);
        message += "<b> " + externalSystem + "</b>" + " Id: " + externalId + ", Silk PLM Id: " + encodedSilkId + "<br>";
      }
      if (idMappingList.length) {
        await DialogManager.info($tr('integrations', 'Integrations'), message);
      } else {
        await DialogManager.info($tr('integrations', 'Integrations'),
            $tr('noConnectionFound', 'No connection found'));
      }
    },

    async openImportFile(disableImport) {
      if (this.form.formTitle === $tr('products', 'Products')) {
        await this.importOrUpdateProductsDialog();
        this.$refs.grid.selectMultiselectNone();
      } else if (this.form.formClassLong === await OrderDetailsPackageRow.getRemoteClass() && disableImport) {
        DialogManager.info($tr('importDisabled', 'Import disabled'),
            $tr('entitiesList.cant.import.packages', "Can't import packages when there are any rows in the table")).then();
      } else {
        let importFormDataActivity = await ActivityManager.showActivity(ImportFormData, {
          title: this.form.formTitle,
          formClassLong: this.form.formClassLong,
          masterId: this.detailsMasterId
        });
      }
    },

    mountEdit(colDef) {
      if (colDef.fieldConfig.editableByFeatureFlag?.length > 0) {
        let editable = checkIfEnabledByFeatureFlags(colDef.fieldConfig.editableByFeatureFlag);
        if (!editable) {
          return;
        }
      }
      colDef.cssHStyle = {"border-bottom-style": "solid", "border-bottom-color": "lightgreen"};
      colDef.editorComponent = GridCellEditor;
    },

    async handleImagesLimitExceededAndShowNotification() {
      if (this.form.formClassLong === Product.getRemoteClass()) {
        let limit = await Rpc.ExcelExports.findLimitForExportedImages();
        showLocalNotification(new LocalNotification($tr("entitiesList.exportWarningTitle", "Export without images"),
            $tr('entitiesList.exportWarningMessage',
                'You have selected more than #{1} records; therefore, the XLSX file export will exclude images.', limit)
        ));
      }
    },

    async importOrUpdateProductsDialog() {
      let result = await DialogManager.importOrUpdateProduct();
      if (!result) {
        return;
      }
      let operationType = result.operationType;
      let updateProductsInSeasonData = result.updateProductsInSeasonData;
      let selectedSeason = result.season ? result.season : null;
      let title = result.season ? operationType + " " + selectedSeason.season : operationType;

      await ActivityManager.showActivity(ImportFormData, {
        title: title,
        uploadData: async (file, fileUpload) => await Rpc.plm.DataImport.importDataToProducts(file, operationType, updateProductsInSeasonData, selectedSeason ? selectedSeason.id : null).lock().onProgress((e) => {
          fileUpload.setProgress(e.percent);
          this.percentDone = Math.floor(e.percent);
        })
      });
    },

    afterDeleteEntity() {
      this.selectionLocal = null;
      this.selectionSize = 0;
      this.$refs.grid.afterDeleteEntity();
    },

    async setDisabledForToolboxButtonsConnectedToSupplierMarket() {
      if (this.form.formClassLong === PlanningProduct.getRemoteClass() ||
          this.form.formClassLong === PPSamplesProduct.getRemoteClass()) {

        let selectLocal = this.selectionLocal;
        if (selectLocal.materialize) {
          await selectLocal.materialize();
        }

        let tmpSelectionLocal = [];
        for (let item of selectLocal) {
          tmpSelectionLocal.push(item);
        }

        const validItems = tmpSelectionLocal.filter(item => !item?.supplierIdRemovedAndBlockedRoleSupplier);

        if (validItems.length === 0) {
          if (this.sendOfferButton) {
            this.sendOfferButton.disabled = true;
          }
          if (this.sendAttachmentsButton) {
            this.sendAttachmentsButton.disabled = true;
          }
          return;
        }

        const allSendInquiry = validItems.every(item => item?.isSendInquiry);
        const noneSendInquiry = validItems.every(item => !item?.isSendInquiry);

        if (allSendInquiry) {
          if (this.sendOfferButton) {
            this.sendOfferButton.disabled = true;
          }
          if (this.sendAttachmentsButton) {
            this.sendAttachmentsButton.disabled = false;
          }
        } else if (noneSendInquiry) {
          if (this.sendOfferButton) {
            this.sendOfferButton.disabled = false;
          }
          if (this.sendAttachmentsButton) {
            this.sendAttachmentsButton.disabled = true;
          }
        } else {
          if (this.sendOfferButton) {
            this.sendOfferButton.disabled = true;
          }
          if (this.sendAttachmentsButton) {
            this.sendAttachmentsButton.disabled = true;
          }
        }
      }
    }
  }
  ,

  props: {
    resultList: null,
    customDataLoader: {
      default:
          null
    },
    customItems: {
      default:

          function () {
            return []
          }
    },
    form: {
      type: BaseFormConfig
    },
    gridName: {
      type: String
    },
    rowDblclicked: Function,
    rowSelected: Function,
    rowsSelected: Function,
    rowIndexSelected: Function,
    rowsIndicesSelected: Function,
    selectionClear: Function,
    beforeSorting: Function,
    afterSorting: Function,
    listOfDynamicPreparedObjectForColumns:
        {
          type: Array,
          default:
              () => []
        },
    uniqueDataForColoringDynamicColumns: {
      type: Set,
      default:
          null
    },
    dblClickEnabled: {
      type: Boolean,
      default:
          false
    },
    selectMode: {
      type: String,
      default:
          "single"
    }, // range
    reloadDataForDynamicTab: {
      default:
          null
    },
    showHeader: {
      type: Boolean,
      default:
          true
    },
    headerShowReload: {
      type: Boolean,
      default:
          false
    },
    headerShowSearch: {
      type: Boolean,
      default:
          true
    },
    searchBarClass: {
      type: String,
      default:
          ""
    },
    editMultiSelectFromTab: {
      default:
          false
    },
    /* sync back props */
    selectedItem: {
      type: Object,
      default:
          null
    },
    selectedItems: {
      type: Array,
      default:
          null
    },
    selection: {
      default:
          null
    },
    selectedIndex: {
      type: Number,
      default:
          null
    },
    selectedIndices: {
      type: Array,
      default:
          null
    },
    filtersColsClasses: {
      type: String,
      default:
          "col-auto"
    },
    detailsMasterFormConfig: {
      type: BaseFormConfig
    },
    detailsConfig: {
      type: FormDetailsConfig,
      default:
          null
    },
    detailsMasterId: {
      type: String,
      default:
          null
    },
    detailsMode: {
      type: String,
      default:
          ""
    },
    detailsTemporaryList: {
      type: TemporaryList,
      default:
          null
    },
    metaStatus: {
      type: Boolean,
      default:
          false
    },
    columnsMixins: {
      type: Array,
      default:
          () => []
    },
    excludedIds: {
      default:
          null, type:
      Array, required:
          false
    },
    onlyIds: {
      default:
          null, type:
      Array, required:
          false
    },
    formValidatedFieldWithParent: {
      default:
      undefined
    },
    toolboxMixins: {
      type: Array,
      default:
          () => []
    },
    filter: {
      default:
          null
    },
    showAdd: {
      type: Boolean,
      default:
          false
    },
    showDelete: {
      type: Boolean,
      default:
          false
    },
    showDetails: {
      type: Boolean,
      default:
          true
    },
    filteringMode: {
      type: Boolean,
      default:
          false
    },
    addEnabled: {
      default:
          true
    },
    context: {
      default:
          null
    },
    recordsWithChosenFilters: {
      default:
          null
    },
    showColumns: {
      default:
          true
    },
    streamRowsPostProcess: {
      default:
          null
    }
  },
  model: {
    prop: 'selection',
    event:
        'model-change',
  },

  watch: {
    'form': function (val) {
      this.setForm(val);
    },
    'selectionLocal': function (val) {
      this.$emit("model-change", val);
      afterChangeSelectionLocalSetDisabledForToolboxButtons(this.form, this.toolbox, this.selectionLocal);
      this.setDisabledForToolboxButtonsConnectedToSupplierMarket(val);
    },
    'detailsTemporaryList': function (val) {
      this.refresh();
    },
    metaStatus: function (val) {
      this.refreshColumns();
    },
    selectMode: function (val) {
      this.refreshColumns();
    },
    selectionSize: function (val) {
      this.$emit("update:selectionSize", this.selectionSize);
    },
    showMultiSelect: function (val) {
      this.$emit("update:showMultiSelect", this.showMultiSelect);
    },
    editMultiSelectFromTab: function (val) {
      this.$emit("update:editMultiSelectFromTab", val);
    },
    activeFilters: function (val) {
      SessionManager.configManager?.save("grid.active.filters." + this.gridId, val, SCOPE_DEVICE_SIZE);
    },
    activeDynamicFilters: function (val) {
      SessionManager.configManager?.save("grid.active.dynamic.filters." + this.gridId, val, SCOPE_DEVICE_SIZE);
    },
    reloadDataForDynamicTab: function (val) {
      this.$emit("update:reloadDataForDynamicTab", this.reloadDataForDynamicTab);
    },
    listOfDynamicPreparedObjectForColumns: async function (val) {
      await this.readActiveFilters();
      this.setupFilters();
    },
    dynamicRows: function (val) {
      if (val.length > 0) {
        this.disableImport = true;
      } else {
        this.disableImport = false;
      }
    },
  },
  computed: {
    fields: function () {
      let fields = [];
      this.form.fields.forEach(formField => {
        let enabledFieldBySettings = EntityEditFormService.checkIfEnabledByOrganizationFormsSettingsForFormField(this.form, formField);
        if (!enabledFieldBySettings) {
          return;
        }
        let enabledField = EntityEditFormService.checkIfEnabledByFeatureFlags(formField.enabledByFeatureFlags);
        if (!enabledField) {
          return;
        }
        if (formField.gridColDisabled) {
          return;
        }
        if (this.detailsConfig?.joiningField === formField.fieldName) {
          return;
        }
        /** @type {ColumnDefinition} */
        let colDef = {};

        colDef.name = formField.gridColLabel;
        colDef.prop = formField.fieldName;
        colDef.autoSize = formField.gridColAutosize;
        colDef.sortable = formField.gridColSortable;
        colDef.searchable = formField.gridColSearchable;
        colDef.size = formField.gridColSize;
        colDef.visible = formField.gridColVisible;
        colDef.id = colDef.prop;
        colDef.fieldConfig = formField;
        const hexColor = formField.hexColor;
        //colDef.cssHStyle = formField.hexColor ? {"border-bottom-style": "solid", "border-bottom-color": hexColor} : null;
        colDef.formConfig = this.form;

        colDef.gridOrder = findGridColOrderForFormField(this.form, formField);

        let fieldFromMaster = true;

        if (colDef.formConfig.isView) {
          if (this.form.isView) {
            this.masterFormConfigFromView = FormsLoader.detectFormConfig(this.form.masterClass);
          }
          if (colDef.formConfig.rootClassShortId) {
            let fieldNameFromMaster = formField.fieldName.slice(formField.fieldName.lastIndexOf("_") + 1);
            let foundFieldFromMaster = false;
            for (let f of this.masterFormConfigFromView.fields) {
              if (f.fieldName === fieldNameFromMaster) {
                foundFieldFromMaster = true;
                break;
              }
            }
            if (!foundFieldFromMaster) {
              fieldFromMaster = false;
            }
          }
        }
        if (formField.gridColEditable && fieldFromMaster) {
          if (formField.securedFieldConfig && formField.securedFieldConfig?.restrictedWrite) {
            if (this.permissions[formField.securedFieldConfig.effectivePermissionWrite]) {
              if (this.editable) {
                if (this.form.isView) {
                  if (this.form.editableFields.length) {
                    const isEditableField = _.includes(this.editableViewFields, formField.fieldName);
                    if (isEditableField) {
                      this.mountEdit(colDef);
                    }
                  } else {
                    this.mountEdit(colDef);
                  }
                } else {
                  this.mountEdit(colDef);
                }

              }
            }

          } else if (!formField.readOnly) {
            if (this.permissions[this.effectivePermissionWrite]) {
              if (this.editable) {
                if (this.form.isView) {
                  if (this.form.editableFields.length) {
                    const isEditableField = _.includes(this.editableViewFields, formField.fieldName);
                    if (isEditableField) {
                      this.mountEdit(colDef);
                    }
                  } else {
                    let mountEdit = true;
                    // TODO w przyszłości podpiąć lockedFieldsy pod widoki
                    if (this.form.formClassLong === "com.silkplm.model.plm.ProductInSeasonColorVersionView") {
                      if (!SessionManager.hasFlag(FeatureFlag.AUTOMATIC_CALCULATION_OF_QUANTITY_ON_PRODUCT_IN_SEASON)) {
                        if (formField.fieldName === this.form.rootClassShortId + "_totalQuantityToOrder") {
                          mountEdit = false;
                        }
                      }
                    }
                    if (mountEdit) {
                      this.mountEdit(colDef);
                    }
                  }
                } else {
                  this.mountEdit(colDef);
                }
              }
            }
          }
        }
        if (formField.gridColTemplateComponent) {
          colDef.template = Renderers[formField.gridColTemplateComponent];
        } else if (formField.gridColTemplate) {
          colDef.template = formField.gridColTemplate;
        } else if (formField.gridToStringFunction) {
          if (!RtModule().hasFunction(formField.gridToStringFunction)) {
            Log.warn("No function found:" + formField.gridToStringFunction, true);
          } else {
            const fun = RtModule()[formField.gridToStringFunction];
            colDef.toStr = (val) => {
              return fun.call(this.form, val, formField, this.form)
            };
          }
        } else if (formField.isForeignKey) {
          colDef.toStrFromInstance = async (/** @type {RpcClass} */row) => {
            try {
              return await row.getFkFieldLabel(formField.fieldName, true);
            } catch (e) {
              throw e;
            }
          };
        } else if (formField.optionsType === OPTIONS_FK_DYNAMIC) {
          colDef.toStrFromInstance = async (/** @type {RpcClass} */row) => {
            try {
              return row.__options__?.[formField.fieldName] ?? row[formField.fieldName];
            } catch (e) {
              throw e;
            }
          };
        }

        if (formField.filterToStringFunction) {
          if (!RtModule().hasFunction(formField.filterToStringFunction)) {
            Log.warn("No function found:" + formField.filterToStringFunction, true);
          } else {
            const fun = RtModule()[formField.filterToStringFunction];
            colDef.toFilterStr = (val) => {
              return fun.call(this.form, val, formField, this.form)
            };
          }
        } else {

          if (formField.isForeignKey) {
            colDef.toFilterStr = (item, row) => {
              try {
                return row.getFkFieldLabel(formField.fieldName, true);
              } catch (e) {
                throw e;
              }
            };
          } else if (formField.optionsType === OPTIONS_FK_DYNAMIC) {
            colDef.toFilterStr = (item, row) => {
              try {
                return row.__options__?.[formField.fieldName] ?? row[formField.fieldName];
              } catch (e) {
                throw e;
              }
            };
          }
        }

        fields.push(colDef);

      });
      const indexesToRemove = [];
      for (let index = 0; index < fields.length; index++) {
        let field = fields[index];
        if (field.fieldConfig?.onGridShowAsField?.length) {
          let onGridShowAsFieldName = field.fieldConfig.onGridShowAsField;
          if (this.form.isView) {
            // to jest źle, bo nie może być rootClassShortId tylko powinien być member of form, ale to jest tricky do zrobienia
            // bo dla przykładu dla SampleView jedyny members to root (brak membera dla Produktu, a jeśli rozszerze o extension też będzie źle,
            // dlatego użyje includes zamaist tego, ale to nie jest do konca poprawne - trzeba na to uwazać żeby nie trafiło się inne pole o tym samym includes
            // onGridShowAsFieldName = this.form.rootClassShortId + "_" + field.fieldConfig.onGridShowAsField
            let filteredField = fields.filter(el => el.prop.includes(onGridShowAsFieldName))[0]; // jeśli nie będzie to od razu exception
            onGridShowAsFieldName = filteredField.prop;
          }
          let indexFromOverride = _.findIndex(fields, ['prop', onGridShowAsFieldName]);
          if (indexFromOverride !== -1) {
            let cloned = _.cloneDeep(fields[indexFromOverride]);
            let originalFieldName = fields[index].fieldConfig.fieldName;
            fields[index] = cloned;
            fields[index].fieldConfig.fieldName = originalFieldName;
            indexesToRemove.push(indexFromOverride);
          }
        }
      }
      // We remove indices from highest to lowest to avoid shifting the remaining ones
      indexesToRemove.sort((a, b) => b - a).forEach(i => {
        fields.splice(i, 1);
      });
      fields.sort((a, b) => {
        return a.gridOrder - b.gridOrder
      });

      _.forEachRight(this.columnsMixins, (/** @type {ColumnMixin} */ mixin) => {
        /** @type {ColumnDefinition} */
        let clone = _.clone(mixin.column);
        /** @type {string} */
        const after = mixin.after;

        if (!after) {
          fields.splice(0, 0, clone);
          return;
        }

        const propId = after.toLowerCase().replaceAll(" ", "");
        for (let i = 0; i < fields.length; i++) {
          if (fields[i].prop.toLowerCase().replaceAll(" ", "") === propId) {
            fields.splice(i, 0, clone);
            return;
          }
        }

        fields.push(clone);
      });

      if (this.form.classIsDynamic) {
        if (this.listOfDynamicPreparedObjectForColumns.length > 0) {
          _.forEach(this.listOfDynamicPreparedObjectForColumns, (dynamicColumn) => {

            if (this.uniqueDataForColoringDynamicColumns !== null) {
              if (dynamicColumn.editable) {
                dynamicColumn.editorComponent = GridCellEditor;
                dynamicColumn.cssHStyle = {"background": `rgb(${dynamicColumn.customCssHStyleValue}, 211, 255)`};
                if (this.editable) {
                  dynamicColumn.cssHStyle = {
                    "border-bottom-style": "solid",
                    "border-bottom-color": "lightgreen",
                    "background": `rgb(${dynamicColumn.customCssHStyleValue}, 211, 255)`
                  };
                }
              }
            }

            if (dynamicColumn.base) {
              dynamicColumn.cssHStyle = {"background": "#d8ecfc"};
              if (this.editable && dynamicColumn.editable) {
                dynamicColumn.editorComponent = GridCellEditor;
                dynamicColumn.cssHStyle = {
                  "border-bottom-style": "solid",
                  "border-bottom-color": "lightgreen",
                  "background": "#d8ecfc"
                };
              }
            } else if (this.uniqueDataForColoringDynamicColumns === null) {
              dynamicColumn.cssHStyle = {"background": "#C9D2FF"};
              if (this.editable && dynamicColumn.editable) {
                dynamicColumn.editorComponent = GridCellEditor;
                dynamicColumn.cssHStyle = {
                  "border-bottom-style": "solid",
                  "border-bottom-color": "lightgreen",
                  "background": "#C9D2FF"
                };
              }
            }

            fields.push(dynamicColumn);
          });

        }
      }

      return fields;
    }
  },

  data: function () {

    let toolbox = [];
    let classMixins = getCustomToolboxMixinsForList(this.form, this, this.selectionLocal);

    return {
      editableViewFields: [],
      popupContainer: null,
      languageOptions: [],
      sendAttachmentsButton: null,
      sendOfferButton: null,
      streaming: false,
      addOrderForMaterialsButton: null,
      pendingQueryId: 0,
      eventSequenceId: 0,
      lastEventSequenceId: 0,
      refreshInProgress: false,
      currentQuerySignature: "",
      selectionSize: 0,
      clusterFieldName: "",
      clusterVal: "",
      hasFilters: false,
      filterData: this.filter,
      hasCluster: false,
      permissions: new PermissionsSet(),
      selectionLocal: null,
      /** @type {DataGrid2|Vue} */
      grid: null,
      gridId: null,
      lists: this.context?.list ?? [],
      formInitialized: false,
      editable: false,
      allSelectedFilters: [],
      allFilters: [],
      activeFilters: [],
      activeDynamicFilters: [],
      eligibleFields: null,
      activity: null,
      editButton: null,
      toolbox: [
        ...toolbox,
        ...this.toolboxMixins,
        ...classMixins
      ],
      addNextFilter: true,
      showMultiSelect: false,
      tabName: false,
      dataForSummaryRow: [],
      effectivePermissionWrite: null,
      masterFormConfigFromView: null,
      recordsWithChosenFiltersInStreamingMode: [],
      streamingEngine: null,
      dynamicRows: null,
      colForSearch: null,
      disableImport: false,
      shouldRemember: false,
      language: null,
      result: null,
      loadInProgress: false,
    }
  }
}
</script>

<style scoped>

</style>